/**
 * Mirror types of the FastAPI backend schemas (TRD-06 + TRD-19).
 *
 * These intentionally hand-authored to keep the FE bundle independent of
 * the BE OpenAPI generator (which would pull in pydantic types). Keep in
 * sync with eduviaos-backend/app/domains/X/schemas.py. A type-drift CI
 * check lands in Phase 0c.
 */

// --- Auth (eduviaos-backend/app/domains/auth/schemas.py) -----------------

export type UserRole =
  | "super_admin"
  | "owner"
  | "admin"
  | "teacher"
  | "staff"
  | "student"
  | "parent";

export type UserStatus = "active" | "suspended" | "pending_verification";
export type PermissionMode = "additive" | "replace";

export interface UserPublic {
  id: string;
  tenant_id: string;
  email: string;
  full_name: string | null;
  role: UserRole;
  status: UserStatus;
  branch_id: string | null;
  last_login_at: string | null;
  /** Learner profile photo (SC-14c). */
  avatar_url?: string | null;
  /** Tenant-defined display label (e.g. "Principal", "HOD"). TRD-02 §2.2. */
  designation: string | null;
  designation_id?: string | null;
  access_inherits_designation?: boolean;
  permission_mode?: PermissionMode;
  /**
   * Permissions granted on top of the role's defaults, clamped to the
   * role's ceiling. The resolved effective set lives at
   * `GET /auth/me/permissions`; this raw list is provided for admin UIs
   * that need to round-trip the override values themselves.
   */
  permission_overrides: string[];
}

/** Resolved permission set for the current user — TRD-02 §2.4. */
export interface PermissionsResponse {
  user_id: string;
  tenant_id: string;
  role: UserRole;
  designation: string | null;
  /** Sorted alphabetically; safe to use as a cache key. */
  permissions: string[];
}

export interface AccessToken {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
}

export interface LoginResponse {
  user: UserPublic;
  tokens: AccessToken;
}

export interface TenantFeatureFlagsView {
  /**
   * Phase B2 — when False, the institution admin's Forms nav entry is
   * hidden and `MANAGE_FORMS` API requests return 403. The SA / account
   * manager edits forms cross-tenant via the SA portal.
   */
  forms_editable_by_tenant: boolean;
  /**
   * Phase H5d — when False, the institution admin's assessment template
   * authoring controls (New template / Edit / Activate / Archive) are
   * hidden and every write under `/assessments/*` returns 403
   * `FORBIDDEN` with `details.code == "FEATURE_FLAG_DISABLED"` for
   * `admin`-role users. Reads (list / get / availability / pool stats)
   * stay open so the IA can still browse the question bank + templates
   * while authoring is paused.
   *
   * The SA flips this via the SA portal Tenant Detail → Settings tab
   * (`PATCH /super-admin/tenants/{slug}/feature-flags`). The SA can
   * always author templates cross-tenant via the SA portal Settings →
   * Assessment templates surface (Phase H5c).
   */
  assessments_authoring_by_tenant: boolean;
  /**
   * Phase F-finale-6 — when False, the institution admin's admission
   * funnel editor (nav entry + stage/policy/requirement write controls)
   * is hidden and every write under `/admission-stages` returns 403
   * `FORBIDDEN` with `details.code == "FEATURE_FLAG_DISABLED"` for
   * `admin`-role users. Reads (`GET /admission-stages` + `/funnel`) stay
   * open so the IA can still view the funnel while editing is paused.
   *
   * The SA flips this via the SA portal Tenant Detail → Settings tab
   * (`PATCH /super-admin/tenants/{slug}/feature-flags`) and edits the
   * funnel cross-tenant via the SA Admissions tab when paused. Mirrors
   * `forms_editable_by_tenant`.
   */
  admissions_editable_by_tenant: boolean;
  /**
   * Phase SC-22 — when False, the institution's learner form (field
   * order, labels, visibility, custom fields) is read-only for every
   * tenant role and `PUT /me/learner-form` returns 403
   * `FEATURE_FLAG_DISABLED`. The SA edits it from Tenant Detail.
   * Optional so older BE builds without the flag read as "not allowed".
   */
  learner_form_editable_by_tenant?: boolean;
  /**
   * Phase SC-22 — when False, the institution's team invite form is
   * read-only and `PUT /me/team-form` returns 403 FEATURE_FLAG_DISABLED.
   */
  team_form_editable_by_tenant?: boolean;
}

export interface MeResponse {
  user: UserPublic;
  /**
   * Phase B2 — per-tenant feature flags resolved on the user's tenant.
   * Null for super_admin role (the synthetic platform-tenant has no
   * tenant-scoped flags). FE clients should treat null as "no gating".
   */
  feature_flags: TenantFeatureFlagsView | null;
  /**
   * Phase C1a — IA tenant first-run wizard gate.
   * `false` ⇒ the institution-admin SPA must redirect this user to
   * `/first-run/welcome` before any other route. `true` ⇒ allow normal
   * navigation. `null` for super_admin (their synthetic platform tenant
   * is not gated). FE treats `null` like `true`.
   */
  first_run_complete: boolean | null;
  /**
   * Phase SC-22 — non-null only for a super-admin support-session token.
   * The IA renders its persistent support banner from it. Optional so
   * fixtures written before SC-22 stay valid.
   */
  support_session?: SupportSessionView | null;
}

/** Phase SC-22 — `MeResponse.support_session` + exchange response shape. */
export interface SupportSessionView {
  id: string;
  tenant_id: string;
  tenant_name: string;
  sa_user_id: string;
  sa_email: string;
  reason: string;
  started_at: string;
  expires_at: string;
}

/** Phase SC-22 — `POST /auth/support-session/exchange` response. */
export interface SupportSessionExchangeResponse {
  access_token: string;
  token_type: "bearer";
  expires_in: number;
  support_session: SupportSessionView;
}

/**
 * Phase C1a — `GET /me/first-run` and `POST /me/first-run/complete`
 * share the same compact projection so the IA SPA can store both query
 * results in the same TanStack cache slot.
 */
export interface FirstRunState {
  tenant_slug: string;
  tenant_name: string;
  vertical: string;
  first_run_complete: boolean;
}

// --- Phase F1 — Designations (tenant-scoped RBAC overrides) -------------

/**
 * Tenant-scoped designation referencing a base role + extra permissions.
 * Mirrors `DesignationPublic` on the BE (`app.domains.auth.designations`).
 * The wire shape is the same on every CRUD response — list / create /
 * update all return one of these.
 *
 * `permission_overrides` carries the EXTRA permissions toggled on top of
 * the base role's defaults. The BE clamps both at write time
 * (`AZ_OVERRIDE_EXCEEDS_CEILING`) and at read time (defence in depth) so
 * the FE can render the list verbatim without re-clamping — but it should
 * still display the override count using the catalog ceiling so the count
 * stays sensible if the BE ever ships a new permission the FE doesn't
 * know about yet.
 */
export interface Designation {
  id: string;
  tenant_id: string;
  name: string;
  role: UserRole;
  permission_mode: PermissionMode;
  permission_overrides: string[];
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface DesignationCreateInput {
  name: string;
  role: UserRole;
  permission_mode?: PermissionMode;
  permission_overrides?: string[];
  description?: string | null;
}

export interface DesignationUpdateInput {
  name?: string;
  role?: UserRole;
  permission_mode?: PermissionMode;
  permission_overrides?: string[];
  description?: string | null;
}

export interface DesignationListResponse {
  items: Designation[];
}

// --- Phase F2 — AI-suggested permission overrides for the editor -------

/**
 * Tenant verticals the BE recognises (`app.domains.tenant.models.TenantVertical`).
 * Mirrored verbatim so the suggest-scopes call can pass an explicit
 * vertical override (rare; SA shadow-testing) instead of letting the BE
 * derive it from the auth context's tenant.
 */
export type TenantVertical = "school" | "coaching_institute" | "study_centre";

/**
 * Body for `POST /auth/designations/suggest-scopes`. `vertical` is
 * optional — the BE derives it from the auth context's tenant when the
 * field is absent (defaults to `"school"` if the tenant doc isn't seeded).
 */
export interface DesignationSuggestScopesInput {
  label: string;
  role: UserRole;
  vertical?: TenantVertical;
  locale?: string;
}

/**
 * Response from `POST /auth/designations/suggest-scopes`. Every field
 * is FE-trusted: `suggested_overrides` has been filtered server-side
 * against both the canonical `Permission` catalog AND the role's
 * ceiling, so the editor can pre-fill the chips without doing its own
 * re-validation.
 *
 * `ceiling_truncated` and `unknown_dropped` surface what the AI proposed
 * but the server rejected — render them as a small "Dropped by policy"
 * footnote so admins understand why the suggestion isn't a free lunch.
 *
 * `parse_error` is non-null only when the AI returned non-JSON; the
 * FE renders an "AI couldn't structure a suggestion" hint and the
 * admin proceeds manually (Iron Rule #2 graceful-degradation path).
 */
export interface DesignationSuggestScopesResponse {
  suggested_overrides: string[];
  rationales: Record<string, string>;
  ceiling_truncated: string[];
  unknown_dropped: string[];
  provider: string;
  model: string;
  fallback_depth: number;
  latency_ms: number;
  generated_at: string;
  parse_error: string | null;
}

// --- Phase C1b — staff invites (first-run wizard step 2) ----------------

/**
 * Roles invitable from the IA wizard. Mirrors `STAFF_INVITE_ROLES` on
 * the BE (`app.domains.onboarding.models`). Other portal-side surfaces
 * (Phase 8 user-management, super-admin tools) issue invites for the
 * full role list — that's a different DTO, owned by those phases.
 */
export type StaffInviteRole = "teacher" | "staff";

export type StaffInviteRowStatus =
  | "valid"
  | "duplicate_in_payload"
  | "already_invited"
  | "already_member"
  | "invalid_email"
  | "invalid_role"
  /**
   * Phase G0 — set when ``role=teacher`` and the row arrived without
   * an ``eligibility_payload`` (or with an empty array). The IA wizard
   * surfaces this as a dedicated "Add subject + grade" prompt rather
   * than a generic "Invalid" pill.
   */
  | "missing_eligibility"
  /** Phase SC-22 — a required team-form field was empty or not a listed choice. */
  | "incomplete";

/**
 * Phase G0 — one row of the eligibility payload sent with a teacher
 * invite. Mirrors the BE :class:`StaffInviteEligibilityRow`. The IA
 * wizard collects N rows per teacher; the redeem flow materialises one
 * :class:`TeacherEligibility` per row plus optionally a
 * :class:`ClassTeacherAssignment` when ``is_class_teacher`` is set.
 */
export interface StaffInviteEligibilityRow {
  /** FK string to a non-archived ``Subject``. The redeem flow
   * re-validates that the subject is still live. */
  subject_id: string;
  /** Free-form grade key (e.g. ``"9"``, ``"X"``, ``"prep"``). The G3
   * solver maps these to per-tenant grade configs at run time. */
  grade_ref: string;
  /** Optional FK string to a ``Section``. ``null`` means the teacher
   * can teach this subject to any section of the grade. REQUIRED when
   * ``is_class_teacher`` is set. */
  section_id?: string | null;
  /** When ``true``, the redeem flow also writes a
   * ``ClassTeacherAssignment`` for ``(grade_ref, section_id)``. Forces
   * the wizard to require a non-null ``section_id``. */
  is_class_teacher?: boolean;
  /** Soft solver preference signal. */
  preferred?: boolean;
  /** Optional hard cap on weekly periods this teacher can teach for
   * THIS combo (subject × grade × section). */
  max_periods_per_week_for_this_combo?: number | null;
}

export interface StaffInviteRowInput {
  email: string;
  full_name: string;
  role: StaffInviteRole;
  designation?: string | null;
  branch_id?: string | null;
  /**
   * Phase G0 — required for ``role=teacher``. Each row maps to a
   * :class:`TeacherEligibility` (and optionally a
   * :class:`ClassTeacherAssignment`) materialised at redeem time.
   * Omit / pass an empty array for ``role=staff`` invites.
   */
  eligibility_payload?: StaffInviteEligibilityRow[] | null;
  /**
   * Phase H3 (2026-05-10) — curated permission overrides that will be
   * written to the new :class:`User`.``permission_overrides`` field at
   * invite-redemption time. For ``role=staff`` (which now ships with
   * ZERO defaults), this is the ONLY way the redeemed user gets any
   * capability. The IA wizard surfaces a "Suggest permissions" drawer
   * powered by ``POST /me/first-run/staff-invites/ai-suggest`` to
   * pre-populate this list.
   *
   * The BE re-validates against the role's ceiling (non-blocklisted for
   * staff) and rejects unknown / blocklisted entries with
   * ``AZ_OVERRIDE_EXCEEDS_CEILING`` (HTTP 422). Pass an empty array (or
   * omit) to keep the user at the role default — currently zero for
   * staff, the legacy default set for teacher / admin.
   */
  permission_overrides?: string[];
  /** Shown when the team form marks Phone visible. */
  phone?: string | null;
  /** Extra team-form answers, keyed by the custom field key. */
  custom_fields?: Record<string, string>;
}

export interface StaffInvitePreviewItem {
  row_index: number;
  email: string;
  status: StaffInviteRowStatus;
  message?: string | null;
}

export interface StaffInvitePreviewResponse {
  items: StaffInvitePreviewItem[];
  valid_count: number;
}

export interface PendingStaffInviteItem {
  id: string;
  email: string;
  full_name: string;
  role: string;
  designation: string | null;
  branch_id: string | null;
  expires_at: string;
  last_email_sent_at: string | null;
  email_send_count: number;
  last_email_error: string | null;
  issued_by: string;
  created_at: string;
  /**
   * Phase H3 — overrides curated by the inviting admin. Surfaced in the
   * pending-invites panel so the IA can see at a glance which staff
   * invitees were granted explicit permissions (vs the now-empty staff
   * default set). Empty list when the admin issued the invite without
   * granting any overrides.
   */
  permission_overrides: string[];
}

export interface StaffInviteSendResponse {
  sent: PendingStaffInviteItem[];
  skipped: StaffInvitePreviewItem[];
}

export interface PendingStaffInviteListResponse {
  items: PendingStaffInviteItem[];
  total: number;
}

export interface RevokeStaffInviteResponse {
  id: string;
  revoked: boolean;
}

/**
 * Phase E1a — response from `POST /me/first-run/staff-invites/{id}/resend`.
 * The BE rotates the underlying token (the previous accept URL is now dead),
 * extends the TTL, increments `email_send_count`, and returns the refreshed
 * row so the FE can re-merge it into TanStack cache without a second fetch.
 */
export interface ResendStaffInviteResponse {
  resent: boolean;
  invite: PendingStaffInviteItem;
}

export interface StaffInviteContext {
  tenant_slug: string;
  tenant_name: string;
  tenant_type: string;
  email: string;
  full_name: string;
  role: string;
  designation: string | null;
  expires_at: string;
}

export interface AcceptStaffInviteResponse {
  user: UserPublic;
  tokens: AccessToken;
}

/**
 * Phase SC-4c — learner portal invites.
 *
 * Roster-driven: the admin picks a `Student` that already exists rather
 * than typing a name and email into a blank form, and redeeming the token
 * writes `Student.user_id`. That binding is what lets the learner portal
 * answer "which seat is mine" without exposing the tenant's whole seating
 * chart (see the booking-read lock in SC-4a).
 *
 * The invite row is a `StaffInvite` under the hood — same token, TTL,
 * revoke and resend machinery — so the shapes below intentionally mirror
 * the staff ones, minus role/designation (always `student`, never has one)
 * and plus `student_id` / `accepted_at`.
 */
export interface LearnerInviteRequest {
  student_id: string;
  /**
   * Overrides the address on the roster row. Imported rows frequently
   * have no email, or carry a parent's; when supplied the BE writes it
   * back to the student so the roster stays truthful.
   */
  email?: string | null;
}

export interface LearnerInviteResponse {
  invite: PendingStaffInviteItem;
  student_id: string;
  student_name: string;
}

export type LearnerInviteStatus = "pending" | "accepted" | "expired" | "revoked";

export interface LearnerInviteItem {
  id: string;
  student_id: string;
  email: string;
  full_name: string;
  status: LearnerInviteStatus;
  expires_at: string;
  last_email_sent_at: string | null;
  email_send_count: number;
  last_email_error: string | null;
  accepted_at: string | null;
  created_at: string;
}

/** Every learner invite for the tenant — pending, accepted and revoked. */
export interface LearnerInviteListResponse {
  items: LearnerInviteItem[];
  total: number;
}

export interface LearnerInviteContext {
  tenant_slug: string;
  tenant_name: string;
  tenant_type: string;
  email: string;
  full_name: string;
  expires_at: string;
}

/**
 * Phase H3 (2026-05-10) — body for
 * ``POST /me/first-run/staff-invites/ai-suggest``.
 *
 * The IA wizard fires this on explicit user intent (clicking the
 * "Suggest permissions" button next to a staff row) — never on mount,
 * so the AI bill stays bounded. The BE reuses the F-domain
 * ``suggest_designation_scopes`` chain (Gemini primary + NVIDIA
 * fallback) with the staff-specific ceiling injected as the AI's
 * allow-list, so the response only ever contains permissions inside
 * the staff ceiling.
 *
 * ``label`` is the free-form designation / job title (e.g. "Hostel
 * Warden", "Lab Technician", "Front Desk"). ``role`` is fixed to
 * ``"staff"`` for now — when admin / teacher invites grow AI suggest,
 * this widens to ``StaffInviteRole``. ``locale`` is optional and
 * defaults server-side to the tenant's default locale.
 */
export interface StaffInviteAISuggestRequest {
  label: string;
  role: StaffInviteRole;
  locale?: string;
}

/**
 * Phase H3 (2026-05-10) — response from
 * ``POST /me/first-run/staff-invites/ai-suggest``.
 *
 * Shape mirrors :type:`DesignationSuggestScopesResponse` so the FE can
 * share the suggest-permissions drawer component between the
 * designation editor (existing) and the staff-invite wizard (new).
 *
 * Every entry in ``suggested_overrides`` has been filtered server-side
 * against the canonical permission catalog AND the staff role ceiling
 * (non-blocklisted), so the editor can pre-fill the checkbox list
 * without doing its own re-validation. ``ceiling_truncated`` and
 * ``unknown_dropped`` surface what the AI proposed but the server
 * rejected — render them as a small "Dropped by policy" footnote so
 * admins understand why the suggestion isn't a free lunch.
 *
 * ``parse_error`` is non-null only when the AI returned non-JSON; the
 * FE renders an "AI couldn't structure a suggestion" hint and the
 * admin proceeds manually (Iron Rule #2 graceful-degradation).
 */
export interface StaffInviteAISuggestResponse {
  suggested_overrides: string[];
  rationales: Record<string, string>;
  ceiling_truncated: string[];
  unknown_dropped: string[];
  provider: string;
  model: string;
  fallback_depth: number;
  latency_ms: number;
  generated_at: string;
  parse_error: string | null;
}

// --- Phase G1 — Academic catalogue (Subject + Section CRUD) -----------
//
// Mirror of `eduviaos-backend/app/domains/academic/schemas.py`.
// Consumed by:
//   * The G0 FE invite-staff form (subject + section dropdowns).
//   * The G3 timetable solver inputs.
//   * The Phase F8 assessment template grade-band pickers.
//
// All endpoints are gated behind the same `MANAGE_SETTINGS` permission
// as branches, so an IA admin who already holds branch-edit can author
// subjects + sections without an extra grant.

/** Subject pedagogical kind. Matches the BE `SubjectKind` enum. */
export type SubjectKind =
  | "theory"
  | "lab"
  | "co_curricular"
  | "language"
  | "physical_education"
  | "elective";

/** Stream tag — how the subject relates to the higher-secondary
 * stream split (CBSE / state boards). `none` for primary years and for
 * coaching institutes that don't carry a stream concept. */
export type SubjectStream = "none" | "science" | "commerce" | "arts" | "vocational";

/** How the subject is taught across the timetable when there are
 * multiple sections of a grade. `section` ⇒ one teacher per section;
 * `combined` ⇒ all sections taught together (uncommon, but real for
 * library / sports / morning-assembly slots). */
export type SubjectParallelism = "section" | "combined";

export interface Subject {
  id: string;
  tenant_id: string;
  /** Canonical upper-case ASCII code. Stable across the subject's
   * lifetime — re-issue rather than rename. */
  code: string;
  name: string;
  /** Free-form aliases the wizard / search bar accepts as alternate
   * spellings (e.g. ``["Mathematics", "Math", "Maths"]``). */
  aliases: string[];
  kind: SubjectKind;
  default_periods_per_week: number;
  default_period_length_min: number;
  /** Free-form grade tags this subject is offered in (e.g.
   * ``["9","10"]`` or ``["XI-Sci","XII-Sci"]``). Empty array ⇒
   * applies to every grade. */
  applicable_grades: string[];
  requires_lab_room: boolean;
  parallelism: SubjectParallelism;
  stream_tag: SubjectStream;
  /** ISO 8601 UTC. ``null`` for live subjects. The G0 redeem flow
   * refuses to materialise eligibility for archived subjects. */
  archived_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SubjectListResponse {
  items: Subject[];
  total: number;
}

export interface CreateSubjectRequest {
  code: string;
  name: string;
  aliases?: string[];
  kind?: SubjectKind;
  default_periods_per_week?: number;
  default_period_length_min?: number;
  applicable_grades?: string[];
  requires_lab_room?: boolean;
  parallelism?: SubjectParallelism;
  stream_tag?: SubjectStream;
}

export interface UpdateSubjectRequest {
  name?: string;
  aliases?: string[];
  kind?: SubjectKind;
  default_periods_per_week?: number;
  default_period_length_min?: number;
  applicable_grades?: string[];
  requires_lab_room?: boolean;
  parallelism?: SubjectParallelism;
  stream_tag?: SubjectStream;
}

export interface Section {
  id: string;
  tenant_id: string;
  branch_id: string;
  grade_ref: string;
  name: string;
  display_order: number;
  academic_year_id: string;
  max_students: number | null;
  current_student_count: number;
  default_room_id: string | null;
  archived_at: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface SectionListResponse {
  items: Section[];
  total: number;
}

export interface CreateSectionRequest {
  branch_id: string;
  grade_ref: string;
  name: string;
  academic_year_id: string;
  display_order?: number;
  max_students?: number | null;
  default_room_id?: string | null;
}

export interface UpdateSectionRequest {
  display_order?: number;
  max_students?: number | null;
  current_student_count?: number;
  default_room_id?: string | null;
}

// --- Phase C1c — student CSV import (first-run wizard step 3) ----------

/**
 * Verdict the BE assigns each parsed CSV row before any DB write. Mirrors
 * `StudentImportRowVerdict` on the BE (`app.domains.onboarding.schemas`).
 *
 * - ``fresh`` — row is novel within the tenant; will be inserted on commit.
 * - ``duplicate_email`` — an active student with this email already exists
 *   in the same tenant.
 * - ``duplicate_composite`` — name + DOB + last-7-digits of parent_phone
 *   match an existing student (covers families that share an email).
 * - ``duplicate_in_payload`` — a previous row in the same upload already
 *   matched this row's email or composite key.
 * - ``invalid_row`` — parser-level rejection (bad date, bad gender, blank
 *   required field). The matching diagnostic lives in
 *   `StudentImportPreviewResponse.invalid` keyed by `line_no`.
 */
/**
 * Phase J1 — Students data-plane shapes.
 *
 * Mirror of ``eduviaos-backend/app/domains/students/schemas.py``. Kept
 * separate from the importer types above so the importer and data-plane
 * can evolve independently (the importer flattens to ``Student`` rows but
 * the importer's preview/commit DTOs intentionally don't expose the full
 * ``StudentResponse`` shape).
 */
export type StudentStatus = "active" | "paused" | "graduated" | "withdrawn" | "transferred";

export type StudentGender = "male" | "female" | "non_binary" | "prefer_not_to_say" | "other";

export type StudentNepStage = "foundational" | "preparatory" | "middle" | "secondary";

/** SC-16 — optional per-student learning policy overrides (study centres). */
export type LearningContentLanguage = "en" | "hi";

export interface StudentLearningOverrides {
  max_questions_per_quiz?: number | null;
  default_question_count?: number | null;
  preferred_language?: LearningContentLanguage | null;
  ziggy_variant?: "pink" | "blue" | null;
}

export interface StudentLearningOverridesInput {
  max_questions_per_quiz?: number | null;
  default_question_count?: number | null;
  preferred_language?: LearningContentLanguage | null;
  ziggy_variant?: "pink" | "blue" | null;
}

export interface Student {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  admission_id: string | null;
  admission_number: string | null;
  roll_number: string | null;

  name: string;
  email: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: StudentGender | null;
  primary_language: string;

  parent_id: string | null;
  class_teacher_id: string | null;
  batch_id: string | null;
  grade: string | null;
  section: string | null;

  /**
   * Phase SC-4c — the portal login bound to this roster row, set when the
   * learner redeems their invite. `null` means "no login yet": the roster
   * shows an Invite action, and self-scoped learner reads (`/api/me/seat`)
   * have nothing to resolve.
   */
  user_id: string | null;

  /** Phase SC-18 — roster hydration for IA grant/revoke (study centres). */
  app_access_granted?: boolean | null;

  status: StudentStatus;
  admission_date: string | null;
  terminating_date: string | null;
  is_alumni: boolean;

  blood_group: string | null;
  aadhaar_reference: string | null;
  stage: StudentNepStage | null;

  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;

  /** SC-16 / SC-19 — optional per-student learning policy overrides. */
  learning_overrides?: StudentLearningOverrides | null;
}

export interface StudentListFilters {
  branch_id?: string;
  status?: StudentStatus;
  batch_id?: string;
  grade?: string;
  section?: string;
  parent_id?: string;
  class_teacher_id?: string;
  limit?: number;
  offset?: number;
}

export interface StudentListResponse {
  items: Student[];
  total: number;
}

// =====================================================
// Phase J1.b — Students writes (mirrors of
//   eduviaos-backend/app/domains/students/schemas.py::
//     CreateStudentRequest / UpdateStudentRequest /
//     TransitionStudentRequest)
// =====================================================

/**
 * Body for `POST /api/students/`. All fields except `name` are optional.
 *
 * `admission_id` and `admission_date` are wired by the BE during convert
 * flows (admission → student) so the IA-admin create form deliberately
 * omits them — admins use this surface to seed pre-funnel students
 * (e.g. legacy roster import where the admission predates this system).
 */
export interface StudentCreateInput {
  name: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  gender?: StudentGender | null;
  primary_language?: string;

  admission_id?: string | null;
  admission_number?: string | null;
  roll_number?: string | null;
  branch_id?: string | null;

  parent_id?: string | null;
  class_teacher_id?: string | null;
  batch_id?: string | null;
  grade?: string | null;
  section?: string | null;

  blood_group?: string | null;
  aadhaar_reference?: string | null;
  stage?: StudentNepStage | null;
  /** ISO date. Omitted → the BE stamps today (IST). */
  admission_date?: string | null;

  metadata?: Record<string, unknown>;
  learning_overrides?: StudentLearningOverridesInput | null;
}

/**
 * Body for `PATCH /api/students/{id}`. `status` is intentionally absent —
 * status transitions go through the dedicated `/transition` endpoint so
 * the audit + lifecycle side-effects (terminating_date, is_alumni) stay
 * consistent.
 */
export interface StudentUpdateInput {
  name?: string;
  email?: string | null;
  phone?: string | null;
  date_of_birth?: string | null;
  gender?: StudentGender | null;
  primary_language?: string;

  parent_id?: string | null;
  class_teacher_id?: string | null;
  batch_id?: string | null;
  grade?: string | null;
  section?: string | null;
  roll_number?: string | null;
  admission_date?: string | null;

  blood_group?: string | null;
  aadhaar_reference?: string | null;
  stage?: StudentNepStage | null;

  metadata?: Record<string, unknown> | null;
  learning_overrides?: StudentLearningOverridesInput | null;
}

/**
 * Body for `POST /api/students/{id}/transition`. `reason` is required
 * (BE rejects empty strings with 422); the audit row that gets written
 * keeps the reason verbatim, so the FE should encourage a one-line
 * sentence over a single word.
 */
export interface StudentTransitionInput {
  new_status: StudentStatus;
  reason: string;
  terminating_date?: string | null;
}

export type StudentImportRowVerdict =
  | "fresh"
  | "duplicate_email"
  | "duplicate_composite"
  | "duplicate_in_payload"
  | "invalid_row";

export type StudentImportSourceLiteral = "csv" | "xlsx";

export interface StudentImportInvalidRow {
  line_no: number;
  field: string;
  message: string;
}

export interface StudentImportPreviewItem {
  line_no: number;
  name: string;
  email: string | null;
  verdict: StudentImportRowVerdict;
  duplicate_of_student_id: string | null;
  notes: string | null;
}

export interface StudentImportSummary {
  total_rows: number;
  fresh_rows: number;
  duplicate_rows: number;
  invalid_rows: number;
}

export interface StudentImportPreviewResponse {
  items: StudentImportPreviewItem[];
  invalid: StudentImportInvalidRow[];
  /** Mapping of CSV header → canonical field. Lets the FE show
   * "Mapped 8 of 10 columns" hints + flag unrecognised headers. */
  header_map: Record<string, string>;
  summary: StudentImportSummary;
}

export interface StudentImportCommitResponse {
  /** ID of the `StudentImportBatch` audit doc — needed for the future
   * per-batch rollback CTA (deferred to C1d). */
  batch_id: string;
  summary: StudentImportSummary;
  imported_student_ids: string[];
  /** Rows the BE skipped (duplicates, when `skip_duplicates=true`). */
  skipped: StudentImportPreviewItem[];
  invalid: StudentImportInvalidRow[];
}

/**
 * Phase H6b — one institute-authored extra column projected from the
 * active ``student_record`` :class:`FormDefinition`. Mirror of
 * ``DynamicColumnInfo`` in
 * ``eduviaos-backend/app/domains/onboarding/schemas.py``.
 *
 * The FE renders one input/cell per entry in display order alongside the
 * canonical columns. Fields not relevant to a given ``field_type`` are
 * left ``null`` / empty so the FE renders the appropriate widget without
 * inspecting the form-builder model directly.
 *
 * ``field_type`` mirrors :class:`FieldType` on the BE (lowercase string
 * union — ``"text"`` / ``"email"`` / ``"phone"`` / ``"number"`` /
 * ``"currency"`` / ``"date"`` / ``"datetime"`` / ``"select"`` /
 * ``"multi_select"`` / ``"radio"`` / ``"checkbox"`` / ``"textarea"`` /
 * ``"address"``). Kept as a wide ``string`` here so a new BE field type
 * doesn't crash the FE bundle — unrecognised types render as
 * ``text`` boxes with a "raw value" hint instead of disappearing.
 */
export interface DynamicColumnInfo {
  key: string;
  label: string;
  field_type: string;
  required: boolean;
  options: string[];
  min_length: number | null;
  max_length: number | null;
  min_value: number | null;
  max_value: number | null;
  pattern: string | null;
  pattern_message: string | null;
  placeholder: string | null;
  help_text: string | null;
}

export interface StudentImportTemplateInfoResponse {
  /** Header row of the canonical template, in display order. */
  canonical_columns: string[];
  /** canonical_field → [alias1, alias2, ...]. Empty list means the
   * canonical name is the only accepted spelling. */
  aliases: Record<string, string[]>;
  /** Hard cap; caller must split larger files. */
  max_rows_per_batch: number;
  // ── Phase H6b additions ──────────────────────────────────────────
  /**
   * True when an institute-authored ``student_record`` form is active
   * for the tenant. Drives the FE's "extra columns" panel.
   *
   * Default ``false`` matches the BE Pydantic default: an older FE
   * build talking to an older BE deployment (or a new BE with no form
   * configured) sees this as ``false`` and behaves identically to the
   * pre-H6b contract — canonical columns only.
   */
  has_active_form: boolean;
  /** Slug of the active ``student_record`` form. ``null`` when no
   * form is configured (canonical-only schema in effect). */
  form_slug: string | null;
  /** Version of the active ``student_record`` form. Snapshotted into
   * the response so the FE can warn when the form is republished
   * between template download and commit. */
  form_version: number | null;
  /** Institute-authored extra columns in display order. Empty when
   * ``has_active_form`` is false. */
  dynamic_columns: DynamicColumnInfo[];
  /** Phase SC-22 — canonical column → operator-facing header label in
   * the tenant's vertical wording (e.g. `roll_number` → "Learner ID"
   * for a study centre). Optional for older BE builds. */
  canonical_column_labels?: Record<string, string>;
}

// --- Learner form (eduviaos-backend/app/domains/tenant/learner_form_schemas.py) ---

export type LearnerFormVertical = "school" | "coaching_institute" | "study_centre";
export type LearnerFormStandardFieldKind =
  | "text"
  | "email"
  | "phone"
  | "date"
  | "select"
  | "textarea";
export type LearnerFormFieldStorage = "column" | "metadata";
export type LearnerFormCustomFieldType = "text" | "number" | "date" | "select" | "phone" | "email";

export interface LearnerFormStandardField {
  key: string;
  kind: LearnerFormStandardFieldKind;
  storage: LearnerFormFieldStorage;
  label: string;
  default_label: string;
  visible: boolean;
  required: boolean;
  order: number;
  help_text: string | null;
  /** Locked fields (the learner's name) can't be hidden or made optional. */
  locked: boolean;
}

export interface LearnerFormCustomField {
  key: string;
  label: string;
  type: LearnerFormCustomFieldType;
  required: boolean;
  visible: boolean;
  order: number;
  help_text: string | null;
  options: string[];
}

export interface LearnerFormResponse {
  vertical: LearnerFormVertical;
  fields: LearnerFormStandardField[];
  custom_fields: LearnerFormCustomField[];
  editable_by_tenant: boolean;
  is_customised: boolean;
  max_custom_fields: number;
}

export interface LearnerFormFieldSettingInput {
  key: string;
  label?: string | null;
  visible?: boolean;
  required?: boolean;
  order?: number | null;
  help_text?: string | null;
}

export interface LearnerFormUpdateRequest {
  fields: LearnerFormFieldSettingInput[];
  custom_fields: LearnerFormCustomField[];
}

export interface LoginRequest {
  email: string;
  password: string;
  tenant_id: string;
}

// --- Capabilities (eduviaos-backend/app/domains/capability/schemas.py) ---

export type Vertical = "school" | "coaching_institute" | "skill_provider" | "library" | "other";

export type CapabilitySource = "branch" | "tenant" | "vertical_default";

export interface CapabilityValue {
  capability_key: string;
  payload: Record<string, unknown>;
  source: CapabilitySource;
  branch_id: string | null;
  schema_version: number;
  updated_at: string | null;
  updated_by: string | null;
}

export interface CapabilitiesAggregate {
  tenant_id: string;
  branch_id: string | null;
  vertical: Vertical;
  capabilities: Record<string, CapabilityValue>;
  vertical_excluded: string[];
  cache: "hit" | "miss";
}

// --- Phase D0b — IA dashboard summary ----------------------------------

/**
 * Vertical literal matching the backend's `TenantVertical` enum exactly.
 * Distinct from the legacy `Vertical` type above (which includes
 * `library` / `other` / `skill_provider` from earlier exploration phases
 * and is kept for backwards compat). New dashboard / module-gating
 * code MUST use this stricter union.
 */
export type TenantVerticalLiteral = "school" | "coaching_institute" | "study_centre";

/**
 * Why a dashboard module isn't showing live data — drives the FE's
 * empty-state rendering decisions.
 *
 * - `not_enabled`     ⇒ module not in `enabled_modules`. Hide card AND nav.
 * - `not_implemented` ⇒ enabled but BE has no data plane yet. Show
 *                       "Coming soon" with the planned-phase tag.
 * - `no_data_yet`     ⇒ enabled + implemented + zero rows. Show
 *                       inviting empty state with a CTA.
 */
export type DashboardEmptyStateCode = "not_enabled" | "not_implemented" | "no_data_yet";

export interface DashboardTenantBlock {
  slug: string;
  name: string;
  vertical: TenantVerticalLiteral;
  first_run_complete: boolean;
  created_at: string;
}

export interface DashboardKpis {
  /** People module (D0). */
  students_total: number | null;
  staff_total: number | null;
  /** Admissions module (D0). */
  admissions_open: number | null;
  admissions_new_this_week: number | null;
  /** Fees module — active fee plans (SC-8). */
  fee_plans_active: number | null;
  /** Attendance module — today's check-ins (SC-8). */
  attendance_today_count: number | null;
  attendance_today_percent: number | null;
  /** Operations bookings (study_centre) — null until D5. */
  bookings_active: number | null;
  /** Timetable / batches — null until D4. */
  batches_active: number | null;
}

export interface RecentEnquiryItem {
  id: string;
  student_name: string;
  stage_key: string;
  created_at: string;
  branch_id: string | null;
  student_email: string | null;
  student_phone: string | null;
}

export interface RecentStaffInviteItem {
  id: string;
  email: string;
  full_name: string;
  role: string;
  created_at: string;
  expires_at: string;
}

export interface DashboardRecent {
  admissions_enquiries: RecentEnquiryItem[];
  pending_staff_invites: RecentStaffInviteItem[];
}

export interface TenantDashboardSummary {
  tenant: DashboardTenantBlock;
  enabled_modules: string[];
  primary_modules: string[];
  kpis: DashboardKpis;
  recent: DashboardRecent;
  empty_states: Partial<Record<string, DashboardEmptyStateCode>>;
}

// --- Phase D1 — Admissions module ---------------------------------------
//
// Mirror of `eduviaos-backend/app/domains/admission/schemas.py` (+ the
// new D1a routes: reject / soft-delete / restore + source/q filters).
//
// Stage labels + color hints come from the capability framework via
// `GET /admissions/funnel` — the FE never hardcodes stage strings or
// per-vertical maps; everything is driven by the SA's funnel config.

export type EnquirySource =
  | "walk_in"
  | "online_form"
  | "referral"
  | "phone"
  | "ad_campaign"
  | "other";

export interface AdmissionStageTransition {
  from_stage_key: string | null;
  to_stage_key: string;
  at: string;
  by: string | null;
  note: string | null;
}

// --- Phase D6 — CRM Activity log (subset of AdmissionEnquiry) ---------

/**
 * Mirror of the BE `ActivityType` enum exactly (values, not names).
 * `stage_changed`, `assigned`, and `followup_set` are SYSTEM-ONLY —
 * the BE rejects them on `POST /activities` so the FE filters them
 * out of the user-facing "Add activity" picker.
 */
export type ActivityType =
  | "call"
  | "email"
  | "message"
  | "note"
  | "document_uploaded"
  | "payment_received"
  | "scheduled"
  | "stage_changed"
  | "assigned"
  | "followup_set";

/** Activity types the user can manually pick from the AddActivity drawer.
 * Excludes the system-only types — the BE returns 422 for those. */
export const USER_ACTIVITY_TYPES: ActivityType[] = [
  "call",
  "email",
  "message",
  "note",
  "document_uploaded",
  "payment_received",
  "scheduled",
];

export interface Activity {
  id: string;
  activity_type: ActivityType;
  at: string;
  by: string | null;
  note: string;
  metadata: Record<string, unknown>;
}

export interface AdmissionEnquiry {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  stage_key: string;
  stage_history: AdmissionStageTransition[];
  last_advanced_at: string | null;
  student_name: string;
  student_email: string | null;
  student_phone: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  assigned_to: string | null;
  /** Free-form on the wire so tenants can add custom sources (e.g.
   * "instagram_dec_campaign"). FE renders via a chip; new sources
   * appear automatically without code changes. */
  source: string | null;
  notes: string | null;
  metadata: Record<string, unknown>;
  converted_to_admission_id: string | null;
  created_at: string;
  updated_at: string;
  created_by: string | null;

  // ── Phase D3-D6 additions ───────────────────────────────────────────
  /** Phase D5 — secondary owners (user IDs). Primary owner is
   * `assigned_to`; collaborators get visibility but no exclusive
   * ownership. Empty array (not null) when nobody else is attached. */
  collaborators: string[];
  /** Phase D3 — next scheduled follow-up (ISO 8601 UTC), or null when
   * none is set. Drives "overdue/today/week" filter chips and the
   * calendar view. */
  next_followup_at: string | null;
  /** Phase D3 — short context for the upcoming follow-up
   * (e.g. "Confirm document submission"). ≤500 chars. */
  next_followup_note: string | null;
  /** Phase D6 — append-only CRM activity log. Newest LAST (BE returns
   * insertion order). Render reversed for "newest first" timelines. */
  activities: Activity[];

  /** Phase D4 — soft-delete timestamp (ISO 8601 UTC). `null` for live
   * enquiries; populated when the row is in the bin. The Trash view
   * uses this to render "Deleted on Dec 4 · auto-purges in 23 days"
   * without a second fetch. */
  deleted_at: string | null;

  /** Phase H1 — admin "Allow next stage" stamp.
   *
   * When non-null, points at the *next* stage's `stage_key`. The apply
   * portal renders that stage's form for the parent (instead of the
   * current stage's), and a successful auto-advance through it lands
   * the enquiry in the unlocked target. Single-use: cleared by the
   * BE on the next successful transition (advance / force-advance /
   * reject). The IA detail panel shows a "Next stage allowed" chip
   * when this is set so admins don't double-issue the grant. */
  unlocked_stage_key: string | null;
}

export interface EnquiryListResponse {
  items: AdmissionEnquiry[];
  total: number;
  limit: number;
  offset: number;
}

/** Phase E2b — counsellor recommendation chip on enquiry detail.
 *
 * Returned by `GET /admissions/enquiries/{id}/ai-suggestion`. The shape
 * is a deliberate superset of what the chip needs today AND what every
 * E2c+ AI surface will need (provider attribution, fallback signal,
 * latency for the debug overlay, generated_at for staleness display).
 *
 * Field semantics:
 *   - `provider` / `model` — every AI surface MUST tell the user which
 *     model produced the text, both for trust (DPDPA transparency) and
 *     so support can correlate to the BE's `AICallLog` rows.
 *   - `fallback_depth` — position of the answering provider in the
 *     configured chain. 0 = primary (Gemini), 1 = first fallback (NIM).
 *     The chip renders an "AI degraded" pill when this is > 0.
 *   - `latency_ms` — surfaced for the FE debug overlay (Phase E6) and
 *     for honest "this took ~N s" copy when slow.
 *   - `generated_at` — ISO 8601 UTC timestamp; the chip uses this to
 *     show "generated 5m ago" so a counsellor who left the page open
 *     for 20 minutes re-fetches before acting on stale advice.
 */
export interface AISuggestionResponse {
  suggestion: string;
  provider: string;
  model: string;
  fallback_depth: number;
  latency_ms: number;
  generated_at: string;
}

/** One row of `GET /admissions/funnel` — capability-driven, NOT inferred
 * from observed enquiries. Empty stages are still present (count=0) so
 * the kanban / status filters still render a column. */
export interface AdmissionFunnelStage {
  stage_key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: "admitted" | "rejected" | null;
  /** Hex string ("#10b981") or design-token slug; consumer normalises. */
  color_hint: string | null;
  count: number;
}

export interface AdmissionFunnelResponse {
  tenant_id: string;
  branch_id: string | null;
  stages: AdmissionFunnelStage[];
  /** Sum of all stage counts — convenient for "X enquiries in funnel"
   * widgets without re-summing on the FE. */
  total: number;
}

export interface AdmissionRecord {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  enquiry_id: string;
  admission_number: string;
  student_name: string;
  student_email: string | null;
  student_phone: string | null;
  parent_name: string | null;
  parent_phone: string | null;
  admitted_at: string;
  admitted_by: string | null;
  status: string;
  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface AdmissionListResponse {
  items: AdmissionRecord[];
  total: number;
  limit: number;
  offset: number;
}

// --- Request payloads (D1) ----------------------------------------------

export interface CreateEnquiryPayload {
  /** Optional — server seeds the funnel's first non-terminal stage. */
  stage_key?: string;
  branch_id?: string | null;
  student_name: string;
  student_email?: string | null;
  student_phone?: string | null;
  parent_name?: string | null;
  parent_phone?: string | null;
  assigned_to?: string | null;
  source?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown>;

  /** Phase H1 BE-2 — when the resolved initial stage has a linked
   * FormDefinition the BE now requires the matching payload at
   * create time (otherwise 422 PAYLOAD_MISSING). Map from
   * stage_key → field-values. The IA's EnquiryCreateSheet renders
   * a `FormRenderer` against the linked form and ships the
   * collected values under this key. */
  stage_payloads?: Record<string, Record<string, unknown>>;

  /** Phase H1 BE-2 — companion to `stage_payloads`: file_refs for
   * `file_upload` fields, keyed by stage_key. Same shape as
   * `FileRef[]` everywhere else in the form domain. */
  stage_file_refs?: Record<string, Array<Record<string, unknown>>>;
}

export interface UpdateEnquiryPayload {
  student_name?: string;
  student_email?: string | null;
  student_phone?: string | null;
  parent_name?: string | null;
  parent_phone?: string | null;
  assigned_to?: string | null;
  source?: string | null;
  notes?: string | null;
  metadata?: Record<string, unknown> | null;
}

export interface AdvanceEnquiryPayload {
  to_stage_key: string;
  note?: string;
  payload?: Record<string, unknown>;
}

/** Phase H1 — `POST /admissions/enquiries/{id}/allow-next-stage`.
 *
 * Stamps `unlocked_stage_key` on the enquiry so the apply portal
 * starts rendering the *next* stage's form for the parent (and the
 * auto-advance machinery lets the enquiry cross the stage boundary
 * once the parent submits). The grant is single-use — consumed by
 * the next successful advance — so each transition needs its own
 * issue. Permission: `manage_admissions`. */
export interface AllowNextStagePayload {
  /** Optional — when omitted, the BE infers the next forward stage
   * from the funnel (skipping terminal-rejected, landing on
   * terminal-admitted). Pass an explicit value when the funnel
   * permits skipping (`allow_skip_stages=true`). */
  next_stage_key?: string | null;
  /** Optional free-text rationale; surfaced in the audit log so
   * admins can later explain why they granted the unlock. */
  note?: string | null;
}

export interface AllowNextStageResponse {
  enquiry: AdmissionEnquiry;
  unlocked_stage_key: string;
}

// --- Phase H2 — Apply portal lifecycle ---------------------------------
//
// Mirrors `eduviaos-backend/app/domains/admission/schemas.py` —
// `ApplyPortalWindowDTO`, `ApplyPortalConfigUpdateRequest`, and
// `ApplyPortalConfigResponse`. The IA self-serve editor at
// `/settings/admissions-lifecycle` reads the `Response`, builds an
// `UpdateRequest` locally, and ships the entire config on save
// (wholesale-replace, not partial PATCH — the BE's consistency
// validators are easier to enforce against a complete payload).

/** One scheduled "open" window on the public apply portal.
 *
 * `start_at` / `end_at` are RFC-3339 timestamps with timezone offset
 * (`+05:30`, `Z`, etc.). The BE's wire validator rejects naive
 * datetimes outright — naive is ambiguous across regions and would
 * silently land in the wrong window at gate-evaluation time.
 *
 * `applicable_grades` narrows the window to a subset of grades
 * (compared case-insensitively against the intake form's "grade"
 * field). `null` means the window applies to ALL grades. Empty
 * arrays are rejected by the BE — pass `null` to mean "any grade".
 */
export interface ApplyPortalWindow {
  start_at: string;
  end_at: string;
  applicable_grades: string[] | null;
  label: string | null;
}

/** Request body for `PUT /admissions/apply-portal-config` — wholesale
 * replacement of the tenant's apply portal lifecycle config.
 *
 * `schedule_kind === "always"` ignores `windows` for gating purposes
 * (open whenever `active=true`); `schedule_kind === "scheduled"`
 * requires at least one window and only opens during one of them.
 *
 * `require_email_otp` is read by the public submit pipeline — when
 * `true`, the apply portal demands an OTP-verified email before the
 * intake form can be submitted.
 *
 * `closed_message_html` is rendered (sanitised) on the public apply
 * portal's closed-state empty-state. Plain text is fine; HTML is
 * stripped of unsafe tags before rendering.
 */
export interface ApplyPortalConfigUpdatePayload {
  active: boolean;
  schedule_kind: "always" | "scheduled";
  windows: ApplyPortalWindow[];
  require_email_otp: boolean;
  closed_message_html: string | null;
}

/** Response body for `GET` and `PUT /admissions/apply-portal-config`.
 *
 * `is_open_now` is derived server-side from the same gate evaluator
 * the public intake routes use, so the IA dashboard chip renders an
 * authoritative status without re-implementing the logic. `next_window`
 * is the chronologically next future window, or `null` when the portal
 * is open right now / no future windows are scheduled.
 */
export interface ApplyPortalConfigResponse {
  active: boolean;
  schedule_kind: "always" | "scheduled";
  windows: ApplyPortalWindow[];
  require_email_otp: boolean;
  closed_message_html: string | null;
  is_open_now: boolean;
  next_window: ApplyPortalWindow | null;
}

export interface RejectEnquiryPayload {
  reason: string;
}

export interface ConvertEnquiryPayload {
  metadata?: Record<string, unknown>;
}

// --- List filters (D1 + D3) --------------------------------------------

/** Phase D3 — server-side follow-up bucket for the list. Mirrors the
 * BE's `?followup_filter` query param values exactly. `none` selects
 * enquiries with NO followup set; `any` selects those with ANY
 * followup (regardless of timing). Omit / undefined = no filter. */
export type FollowupFilter = "overdue" | "today" | "week" | "none" | "any";

export interface EnquiryListFilters {
  stage_key?: string;
  assigned_to?: string;
  branch_id?: string;
  source?: string;
  q?: string;
  include_deleted?: boolean;
  /** Phase D4 — return ONLY soft-deleted rows. Backs the Trash view.
   * Implies `include_deleted=true` server-side. */
  only_deleted?: boolean;
  /** Phase D3 — restrict the result to a follow-up bucket. */
  followup_filter?: FollowupFilter;
  limit?: number;
  offset?: number;
}

// --- Phase D3-D6 — request payloads ------------------------------------

export interface SetFollowupPayload {
  /** ISO 8601 UTC. Pass `null` to CLEAR a follow-up (audit log keeps a
   * `followup_set` activity entry with metadata.cleared=true). */
  next_followup_at: string | null;
  next_followup_note?: string | null;
}

export interface AssignEnquiryPayload {
  /** User ID of the new primary owner. Pass `null` to UNASSIGN. */
  assigned_to: string | null;
  /** Optional context for the audit trail. */
  note?: string | null;
}

export interface AddCollaboratorPayload {
  user_id: string;
}

/** Phase D6 — restricted to user-pickable activity types (BE rejects
 * `stage_changed`, `assigned`, `followup_set` from this endpoint). */
export interface AddActivityPayload {
  activity_type: Exclude<ActivityType, "stage_changed" | "assigned" | "followup_set">;
  note: string;
  /** Type-specific extras (e.g. `{ channel: "whatsapp", duration_seconds: 420 }`
   * for a call). Optional. */
  metadata?: Record<string, unknown>;
}

// --- Phase D4 — bulk operations ----------------------------------------

export interface BulkAdvancePayload {
  enquiry_ids: string[];
  to_stage_key: string;
  note?: string | null;
}

// --- Phase D4 — Trash view filter (used by EnquiryListFilters) ---------

export interface BulkAssignPayload {
  enquiry_ids: string[];
  assigned_to: string | null;
  note?: string | null;
}

export interface BulkDeletePayload {
  enquiry_ids: string[];
}

export interface BulkResultItem {
  enquiry_id: string;
  success: boolean;
  /** Stable error code on failure (e.g. `INVALID_TRANSITION`,
   * `NOT_FOUND`). `null` on success. */
  error_code: string | null;
  /** Human-readable reason on failure. `null` on success. */
  error_message: string | null;
}

export interface BulkResultResponse {
  successful: number;
  failed: number;
  results: BulkResultItem[];
}

// --- Phase D5 — workload report ----------------------------------------

export interface WorkloadStaffStats {
  user_id: string;
  user_name: string;
  user_email: string;
  active_enquiries: number;
  overdue_followups: number;
  due_today: number;
  due_this_week: number;
}

export interface WorkloadResponse {
  staff: WorkloadStaffStats[];
  /** Count of active enquiries with no `assigned_to`. */
  unassigned_active: number;
  total_overdue: number;
}

// --- Phase D5e — assignable staff roster -------------------------------

export interface StaffRosterItem {
  user_id: string;
  full_name: string | null;
  email: string;
  role: string;
  designation: string | null;
  branch_id: string | null;
}

export interface StaffRosterResponse {
  items: StaffRosterItem[];
  total: number;
}

// --- Phase D3 — calendar view ------------------------------------------

export interface CalendarItem {
  enquiry_id: string;
  student_name: string;
  parent_name: string | null;
  parent_phone: string | null;
  stage_key: string;
  next_followup_at: string;
  next_followup_note: string | null;
  assigned_to: string | null;
}

export interface CalendarResponse {
  items: CalendarItem[];
  window_start: string;
  window_end: string;
  /** Items in `items` whose `next_followup_at` is < now (server time). */
  overdue_count: number;
}

// --- Phase F0 — Action-gated stage engine ------------------------------

/** One row of the per-requirement breakdown returned by
 * `GET /admissions/enquiries/{id}/stage-status`.
 *
 * Mirrors the BE's `RequirementStatusDTO` exactly. The FE renders
 * exclusively off `status` for colour/icon (NEVER off `kind`) so we
 * can introduce new requirement kinds server-side without an FE
 * release. `kind` is surfaced only for analytics/data-test attrs and
 * for picking the secondary "what does this requirement actually
 * test?" microcopy line. */
export type StageRequirementKind =
  | "form_submission"
  | "document_upload"
  | "assessment_attempt"
  | "interview_completed"
  | "manual_check";

/** Coarse rendering bucket. `in_progress` covers "started but not
 * yet submitted" (e.g. assessment in flight); `blocked` is reserved
 * for the SA-config-broken case (linked form deleted, etc.) and
 * surfaces with an admin-only "config broken — go fix" affordance. */
export type StageRequirementStatus =
  | "complete"
  | "in_progress"
  | "pending"
  | "blocked"
  | "not_applicable";

export interface StageRequirementStatus_DTO {
  key: string;
  kind: StageRequirementKind;
  label: string;
  status: StageRequirementStatus;
  /** Optional short explanation surfaced in the "Why can't I advance?"
   * expandable list (e.g. `"Awaiting parent submission of apply form"`).
   * Always English source-of-truth; localisation happens on the FE. */
  detail: string | null;
}

/** Aggregate verdict for the stage. Drives the chip colour in the
 * enquiry header AND whether the "Advance now" CTA is enabled. */
export type StageVerdict = "pending" | "partially_complete" | "complete" | "not_applicable";

/** `GET /admissions/enquiries/{id}/stage-status` payload — pure
 * read-model, never mutates the enquiry. Safe to poll. */
export interface StageStatusResponse {
  enquiry_id: string;
  stage_key: string;
  stage_label: string | null;
  verdict: StageVerdict;
  requirements: StageRequirementStatus_DTO[];
  /** True iff the calling user holds `admissions:override_stage_gate`
   * AND the funnel allows further advancement from this stage.
   * Drives the visibility of the "Override gate" admin menu item. */
  can_force_advance: boolean;
  /** The `to_stage_key` `advance_if_complete` would advance to. `null`
   * when the current stage is terminal or the funnel has no forward
   * stage. Surfaced so the FE confirm dialog reads "Advance from
   * Application to Assessment?" without a second funnel-lookup call. */
  next_stage_key: string | null;
}

/** `POST /admissions/enquiries/{id}/stage-status:advance-if-complete`
 * payload — idempotent. Always returns a useful body; `advanced=false`
 * means the engine returned no_op (already past, or requirements not
 * yet met). The FE suppresses the success toast on no_op. */
export interface AdvanceIfCompleteResponse {
  enquiry: AdmissionEnquiry;
  advanced: boolean;
  from_stage_key: string | null;
  to_stage_key: string | null;
}

/** Body for `POST /admissions/enquiries/{id}/stage-status:force-advance`.
 *
 * `to_stage_key` is optional — when omitted, the engine advances to the
 * next forward stage in the funnel. Setting it lets an admin skip
 * multiple stages in one move (e.g. enquiry → enrolled when the data
 * arrived offline).
 *
 * `reason` is mandatory and validated server-side (10-2000 chars) so
 * the audit trail can never carry a blank string and the override-rate
 * dashboards have something meaningful to surface. */
export interface ForceAdvancePayload {
  to_stage_key?: string;
  reason: string;
}

export interface ForceAdvanceResponse {
  enquiry: AdmissionEnquiry;
  from_stage_key: string;
  to_stage_key: string;
  reason: string;
}

// --- Phase F0b — Notification triggers (SA/IA-side configuration) -------
// Mirror types of the BE DTOs in
// `eduviaos-backend/app/domains/notification/schemas.py`. The BE allows
// arbitrary `event` strings (see event_catalogue.py for the rationale)
// so we treat it as a plain string and surface `event_is_known` for the
// soft "no events of this name have fired yet" badge.

/** Where the notification ends up. Mirrors the BE
 * `NotificationChannel` enum. `sms` and `webhook` are reserved per the
 * BE notes — the FE renders them as future-disabled choices so admins
 * can preview the road-map without us having to ship a second editor
 * pass when the plumbing lands. */
export type NotificationChannel = "email" | "in_app" | "sms" | "webhook";

/** How the dispatcher resolves who actually receives the message.
 * Mirrors the BE `NotificationRecipientKind` enum exactly. The recipient
 * editor maps each kind to an inline form (e.g. `staff_role` shows a
 * UserRole dropdown; `custom_emails` shows a chip-input) so the IA never
 * has to author raw JSON. */
export type NotificationRecipientKind =
  | "applicant"
  | "parent"
  | "assigned_owner"
  | "collaborators"
  | "staff_role"
  | "staff_designation"
  | "custom_emails";

/** One recipient slot on a NotificationTrigger. `config` is kind-specific
 * (e.g. `{ role: "counsellor" }` for `staff_role`, `{ emails: [...] }`
 * for `custom_emails`). Empty for kinds that resolve from the enquiry
 * (`applicant`, `parent`, `assigned_owner`, `collaborators`). */
export interface NotificationRecipientInput {
  kind: NotificationRecipientKind;
  config: Record<string, unknown>;
}

/** Status of one dispatch attempt against one recipient on one channel.
 * Mirrors the BE `NotificationDispatchOutcome` enum. */
export type NotificationDispatchOutcomeValue = "sent" | "deferred" | "failed" | "dropped";

/** One row in a `NotificationLog.attempts` array. */
export interface NotificationDispatchAttemptDTO {
  attempted_at: string;
  outcome: NotificationDispatchOutcomeValue;
  channel: NotificationChannel;
  recipient: string;
  duration_ms: number;
  error_code: string | null;
  error_message: string | null;
  provider_message_id: string | null;
}

/** One row in the event catalogue dropdown (read-only). */
export interface NotificationEventDescriptor {
  event: string;
  label: string;
  description: string;
  phase: string;
  sample_payload_keys: string[];
}

export interface NotificationEventCatalogueResponse {
  items: NotificationEventDescriptor[];
}

/** `POST /notifications/triggers` body. Mirrors the BE `TriggerCreate`. */
export interface NotificationTriggerCreateInput {
  name: string;
  description?: string | null;
  event: string;
  template_slug: string;
  channels: NotificationChannel[];
  recipients: NotificationRecipientInput[];
  enabled?: boolean;
  conditions?: Record<string, unknown>;
}

/** `PATCH /notifications/triggers/{id}` body — every field optional so
 * the FE can ship a "rename only" call without re-sending the whole bag.
 * Sending an empty channels/recipients array is a 422 (matches create
 * semantics) — the toggle endpoint is the right way to silence a trigger
 * without losing config. */
export interface NotificationTriggerUpdateInput {
  name?: string;
  description?: string | null;
  event?: string;
  template_slug?: string;
  channels?: NotificationChannel[];
  recipients?: NotificationRecipientInput[];
  enabled?: boolean;
  conditions?: Record<string, unknown>;
}

/** Public projection of a NotificationTrigger. `event_is_known` rides
 * on every response so the FE doesn't have to round-trip the catalogue
 * to render its soft "unknown event" warning chip. */
export interface NotificationTrigger {
  id: string;
  tenant_id: string;
  name: string;
  description: string | null;
  event: string;
  event_is_known: boolean;
  template_slug: string;
  channels: NotificationChannel[];
  recipients: NotificationRecipientInput[];
  enabled: boolean;
  conditions: Record<string, unknown>;
  last_dispatched_at: string | null;
  last_failed_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface NotificationTriggerListResponse {
  items: NotificationTrigger[];
  total: number;
}

/** One `NotificationLog` row — the dispatcher writes one per
 * trigger-fire (NOT per recipient). The per-recipient rows are
 * `attempts`. */
export interface NotificationTriggerLog {
  id: string;
  trigger_id: string;
  event: string;
  correlation_id: string | null;
  payload_summary: Record<string, unknown>;
  attempts: NotificationDispatchAttemptDTO[];
  created_at: string;
}

export interface NotificationTriggerLogListResponse {
  items: NotificationTriggerLog[];
  total: number;
}

// --- Phase F13 BE-1b — Integrations (third-party credentials) ----------
//
// Mirror of `eduviaos-backend/app/domains/integrations/schemas.py`.
// `access_token_encrypted` / `refresh_token_encrypted` are NEVER on the
// wire — the BE projection strips them.

export type IntegrationCredentialStatus = "active" | "expired" | "revoked" | "errored";

export interface IntegrationCredentialView {
  id: string;
  provider: IntegrationProvider;
  provider_account_id: string | null;
  provider_account_email: string;
  account_label: string | null;
  status: IntegrationCredentialStatus;
  scopes: string[];
  access_token_expires_at: string;
  connected_by_user_id: string | null;
  last_used_at: string | null;
  last_refreshed_at: string | null;
  revoked_at: string | null;
}

export interface IntegrationCredentialListResponse {
  credentials: IntegrationCredentialView[];
}

// --- Phase F13 — Online interview (Zoom) -------------------------------
//
// Mirror types of `eduviaos-backend/app/domains/online_interview/schemas.py`.
// All admin endpoints are gated behind `MANAGE_ADMISSIONS`. The host
// `start_url` is privacy-sensitive (privilege-escalation vector — anyone
// with the URL joins as host) so it lives only on the immediate
// post-mint response and the dedicated `/host-link` endpoint, both of
// which are audit-logged BE-side.

export type IntegrationProvider = "zoom" | "google_meet";

export type OnlineInterviewStatus = "scheduled" | "started" | "ended" | "cancelled" | "errored";

/**
 * Privacy-safe projection used by list + detail endpoints — never
 * carries `start_url`, `error_snippet`, or any internal audit metadata.
 */
export interface OnlineInterviewView {
  id: string;
  enquiry_id: string;
  stage_key: string;
  provider: IntegrationProvider;
  host_credential_id: string;
  provider_meeting_id: string;
  join_url: string;
  host_email: string | null;
  topic: string;
  agenda: string | null;
  scheduled_for: string;
  duration_minutes: number;
  timezone: string;
  status: OnlineInterviewStatus;
  started_at: string | null;
  ended_at: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancel_reason: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * The above plus the host `start_url`. Returned exactly twice per
 * interview lifecycle — immediately after mint, and on the dedicated
 * audit-logged `/host-link` endpoint. Treat as privilege-escalation
 * sensitive: NEVER persist client-side beyond the action that needed
 * it; clipboard-copy is the canonical UX, not local cache.
 */
export interface OnlineInterviewWithHostLink extends OnlineInterviewView {
  start_url: string;
}

export interface OnlineInterviewListResponse {
  interviews: OnlineInterviewView[];
}

export interface MintMeetingRequest {
  stage_key: string;
  host_credential_id: string;
  topic: string;
  agenda?: string | null;
  /** ISO 8601 UTC. */
  scheduled_for: string;
  duration_minutes: number;
  timezone: string;
}

export interface MintMeetingResponse {
  interview: OnlineInterviewWithHostLink;
}

export interface CancelInterviewRequest {
  reason?: string | null;
}

export interface CancelInterviewResponse {
  interview: OnlineInterviewView;
}

export interface HostLinkResponse {
  interview_id: string;
  provider_meeting_id: string;
  start_url: string;
}

// --- Error envelope (eduviaos-backend/app/shared/errors.py) -------------

export interface ApiErrorEnvelope {
  error: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
}

// --- Phase G3 — AI timetable solver (admin/scheduling) ----------------
//
// Mirror types of `eduviaos-backend/app/domains/scheduling/schemas.py`
// + `eduviaos-backend/app/domains/scheduling/models.py`.
//
// Only the shapes the FE consumes today are mirrored — solver-input
// payload shapes (PeriodGridPayload, SubjectDemandPayload, ...) are
// added when the constraint-sheet UI lands. The list page only
// reads `TimetableJobView` + `TimetableView` and writes the
// `:cancel` / `:publish` / `:archive` action endpoints.

/** Lifecycle of one solver invocation. Mirrors the BE
 * `TimetableJobStatus` StrEnum exactly. */
export type TimetableJobStatusValue = "queued" | "running" | "succeeded" | "failed" | "cancelled";

/** Lifecycle of one generated timetable. Mirrors the BE
 * `TimetableStatus` StrEnum exactly. */
export type TimetableStatusValue = "draft" | "published" | "archived";

/** Weekday literal — mirrors the BE `Weekday` Literal exactly. */
export type SchedulingWeekday = "MON" | "TUE" | "WED" | "THU" | "FRI" | "SAT" | "SUN";

/**
 * Single row in the solver-job listing on the IA dashboard. Mirrors
 * BE :class:`TimetableJobView` exactly.
 *
 * `objective_breakdown` and `conflict_report` are kept as opaque
 * `Record<string, ...>` until the constraint-sheet UI lands — the
 * list page renders only the headline `objective_score` and a
 * red/green pill from `status`.
 */
export interface TimetableJobView {
  id: string;
  tenant_id: string;
  branch_id: string;
  academic_year_id: string;
  name: string;
  description: string | null;
  status: TimetableJobStatusValue;
  timeout_seconds: number;
  /** ISO 8601 UTC. Stamped when the BackgroundTask flips status to
   * RUNNING. Null while QUEUED. */
  started_at: string | null;
  succeeded_at: string | null;
  failed_at: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  /** Wall-clock duration of the solver invocation (seconds). Set on
   * terminal transition. */
  elapsed_seconds: number | null;
  /** Higher is better; null when solver returned feasible-but-no-
   * objective (all soft constraints disabled). */
  objective_score: number | null;
  objective_breakdown: Record<string, number>;
  /** Populated only when status === "failed". Carries
   * { primary_kind, summary, generated_at_iso, blames[] }. */
  conflict_report: Record<string, unknown> | null;
  /** ID of the produced :class:`Timetable` row (DRAFT). Set when
   * status === "succeeded". */
  draft_timetable_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TimetableJobListResponse {
  jobs: TimetableJobView[];
  total: number;
}

/**
 * One row in the saved-timetables panel on the IA dashboard. Mirrors
 * BE :class:`TimetableView` exactly.
 *
 * `period_grid_snapshot` is a frozen :class:`PeriodGrid` dict —
 * opaque on the list view; the detail/grid page parses it into a
 * weekly grid.
 */
export interface TimetableView {
  id: string;
  tenant_id: string;
  branch_id: string;
  academic_year_id: string;
  name: string;
  description: string | null;
  status: TimetableStatusValue;
  period_grid_snapshot: Record<string, unknown>;
  /** ID of the :class:`TimetableJob` that produced this timetable.
   * Null for hand-authored rows (rare; the FE doesn't expose this
   * path today). */
  source_job_id: string | null;
  /** Local-date string `YYYY-MM-DD`. */
  effective_from: string;
  effective_to: string | null;
  /** G3.5 BE-2 — semantic label for the validity window. */
  validity_kind: ValidityKind;
  total_slots: number;
  teacher_count: number;
  section_count: number;
  objective_score: number | null;
  objective_breakdown: Record<string, number>;
  published_at: string | null;
  published_by: string | null;
  archived_at: string | null;
  archived_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface TimetableListResponse {
  timetables: TimetableView[];
  total: number;
}

/**
 * Single :class:`TimetableSlot` — embedded in a timetable detail
 * response. The grid view renders these on a weekday × period grid.
 */
export interface TimetableSlotView {
  id: string;
  timetable_id: string;
  weekday: SchedulingWeekday;
  period_index: number;
  start_minute: number;
  end_minute: number;
  /** Free-form grade key (e.g. ``"9"``, ``"X"``). */
  section_grade: string;
  section_id: string;
  stream_tag: string | null;
  subject_code: string;
  teacher_id: string;
  room_id: string | null;
  is_lab_period: boolean;
  manual_override: boolean;
  overridden_by: string | null;
  overridden_at: string | null;
}

/** Detail response — timetable doc + its slots. */
export interface TimetableDetailResponse {
  timetable: TimetableView;
  slots: TimetableSlotView[];
}

/** PATCH body for a single slot manual-override. At least one of the
 * fields must be set; the BE service layer enforces this. */
export interface ApplyManualOverrideRequest {
  teacher_id?: string | null;
  subject_code?: string | null;
  room_id?: string | null;
  /** When true, the BE clears the slot's existing room. Mutually
   * exclusive with `room_id`. */
  clear_room?: boolean;
}

/** Response envelope for the cancel-job endpoint. */
export interface CancelTimetableJobResponse {
  job: TimetableJobView;
}

/** Soft-constraint feature flags posted to the BE solver. Defaults
 * match the platform's TRD-recommended Indian-school behaviour. */
export interface SoftConstraintTogglesPayload {
  no_consecutive_same_subject?: boolean;
  pe_after_lunch?: boolean;
  no_maths_after_lunch?: boolean;
  lab_two_period_blocks?: boolean;
  class_teacher_continuity?: boolean;
  workload_balance_across_teachers?: boolean;
  /** Fraction (0..1) of the solver's wall-clock budget spent on
   * objective improvement after a feasible solution is found. */
  objective_time_budget_share?: number;
}

/** Phase G3.5 BE-1 — semantic kind for one break in the daily
 * grid. The FE uses this to badge each break with the right icon
 * and colour without inferring from the label. */
export type BreakKind = "short" | "long" | "lunch" | "prayer" | "assembly" | "other";

/** Pattern semantics for a single weekday in the period grid.
 * Mirrors the BE `DayPatternKind` enum exactly. */
export type DayPatternKind = "full" | "half" | "off" | "alternate";

/** Phase G3.5 BE-1 + G3.6 BE-1 — one break in the daily period
 * schedule. Mirrors :class:`BreakConfigPayload` field-for-field. */
export interface BreakConfigPayload {
  /** 1-based anchor — the break fires AFTER period N. `0` means
   * "before period 1" (a pre-period-1 break OR, when period zero
   * is enabled, the slot between zero and period 1). */
  after_period: number;
  duration_min: number;
  label: string;
  kind?: BreakKind;
  /** G3.6 BE-1 — when `true`, the break anchors BEFORE the zero
   * period (assembly / prayer / announcements). Only valid when
   * `QuickGenerateConfigPayload.period_zero` is set with
   * `enabled=true`. */
  before_period_zero?: boolean;
}

/** Phase G3.6 BE-1 — homeroom / class-teacher slot at the start
 * of every working day. Mirrors :class:`PeriodZeroPayload`
 * field-for-field. The class teacher uses the slot for
 * attendance + counselling; gated by the
 * `class_teacher_period_zero` permission. */
export interface PeriodZeroPayload {
  enabled?: boolean;
  /** Duration in minutes. Range [5, 60]; default 15. */
  duration_min?: number;
  /** User-facing label rendered on the IA grid + parent app's
   * "today's schedule" view. Range [1, 40] chars. */
  label?: string;
}

/** Phase G3.6 BE-1 — pre-flight readiness snapshot for the quick-
 * generate flow. Returned by `GET /admin/scheduling/catalogue-
 * readiness`. The FE reads this BEFORE rendering the quick-
 * generate sheet so it can either gate the "New solver job" CTA
 * with a banner listing the missing pieces or let the admin
 * proceed. */
export interface CatalogueReadinessView {
  branch_id: string;
  academic_year_id: string;
  subjects_count: number;
  sections_count: number;
  teachers_with_eligibility_count: number;
  /** How many distinct grades (across all sections in scope) have
   * at least one applicable subject. */
  grades_with_demand_count: number;
  /** `true` ⇔ all four counts > 0 AND every section's grade has
   * at least one applicable subject. */
  ready: boolean;
  /** Stable string codes for the gates that aren't satisfied. */
  missing: Array<"subjects" | "sections" | "teacher_eligibility" | "grade_demand">;
}

/** Phase H4 BE — per-module readiness snapshot returned inside the
 * shell-level catalogue readiness aggregator. `ready` flips to
 * `true` when every gate is satisfied; `missing` carries stable
 * snake_case codes (one per failed gate) so the FE can render a
 * deep-linked banner without locale-specific BE strings. `counts`
 * is a free-form `{ [string]: number }` map that the BE keeps in
 * sync with the gate codes (e.g. `subjects: 0`, `sections: 12`)
 * so the FE can show "0 of N" hints without a second round-trip. */
export interface ModuleReadinessView {
  ready: boolean;
  missing: string[];
  counts: Record<string, number>;
}

/** Phase H4 BE — cross-module readiness payload returned by
 * `GET /me/catalogue-readiness`. Consumed by the IA AppShell
 * BEFORE rendering the sidebar so module nav entries with unmet
 * preconditions can render disabled-with-tooltip and route
 * landing pages can offer a "Configure prerequisites" UI.
 *
 * Scoping: `branch_id` defaults to the tenant's flagship branch
 * (or alphabetically-first ACTIVE branch); `academic_year_id`
 * defaults to the deterministic `{tenant_id}:{calendar_year}`
 * convention. Both are echoed back in the response so the FE can
 * hydrate its scope picker without an extra call.
 *
 * Empty-tenant case: the BE returns `404 NotFoundError` with
 * `code: "READINESS_NO_BRANCH"` when the tenant has zero branches
 * (brand-new account before onboarding) — the FE catches that
 * and renders "Finish onboarding first" copy. */
export interface CatalogueReadinessAggregateView {
  branch_id: string;
  academic_year_id: string;
  timetable: ModuleReadinessView;
  attendance: ModuleReadinessView;
  substitution: ModuleReadinessView;
}

/** Phase G3.5 BE-1 — per-day override for the weekly grid shape.
 * Mirrors :class:`DayPatternPayload`. Days listed in `working_days`
 * without an explicit pattern default to FULL, so admins only need
 * to send entries for days that deviate (half-day Saturday, Friday
 * off, alternate Wed, etc.). */
export interface DayPatternPayload {
  weekday: SchedulingWeekday;
  pattern: DayPatternKind;
  /** `null` ⇒ use the pattern's default
   * (`periods_per_day` for FULL/ALTERNATE,
   * `ceil(periods_per_day / 2)` for HALF, `0` for OFF). */
  periods_override?: number | null;
  /** Only consulted when `pattern === "alternate"`. `"odd"` =
   * 1st, 3rd, 5th occurrences working; `"even"` = 2nd, 4th. */
  alternate_parity?: "odd" | "even";
}

/** Phase G3.5 BE-2 — semantic label for a timetable's validity
 * window. The FE renders this as a chip on the timetable card and
 * lets admins edit it via the quick-generate sheet. Mirrors the BE
 * `ValidityKind` StrEnum exactly. */
export type ValidityKind =
  | "weekly"
  | "fortnightly"
  | "monthly"
  | "term"
  | "semester"
  | "yearly"
  | "custom";

/** Mirrors :class:`QuickGenerateConfigPayload` on the BE. Every
 * field is optional — defaults are CBSE / ICSE friendly.
 *
 * G3.5 BE-1: the modern multi-break + per-day-pattern surface is
 * `breaks` + `day_patterns`. The legacy `saturday_pattern` /
 * `lunch_*` fields are still accepted by the BE but should NOT be
 * sent by new FE code; they remain in this type only for
 * compatibility while old preview tabs flush. */
export interface QuickGenerateConfigPayload {
  working_days?: SchedulingWeekday[];
  periods_per_day?: number;
  period_length_min?: number;
  day_start_min?: number;
  /** G3.5 BE-1 — multi-break authoring. `undefined` ⇒ BE
   * synthesises sensible defaults (short break + lunch). An empty
   * array `[]` explicitly disables all breaks. */
  breaks?: BreakConfigPayload[];
  /** G3.5 BE-1 — per-day overrides. Empty array ⇒ all working days
   * use the FULL pattern. */
  day_patterns?: DayPatternPayload[];
  /** G3.6 BE-1 — optional homeroom slot at the start of every
   * working day. `undefined` or `enabled=false` ⇒ no zero period
   * (matches pre-G3.6 behaviour). */
  period_zero?: PeriodZeroPayload | null;
  /** @deprecated Send `day_patterns` instead. Kept for BE compat. */
  saturday_pattern?: "full" | "half" | "off" | "alternate" | null;
  /** @deprecated Send `breaks` instead. Kept for BE compat. */
  lunch_after_period?: number | null;
  /** @deprecated Send `breaks` instead. Kept for BE compat. */
  lunch_duration_min?: number;
  /** @deprecated Send `breaks` instead. Kept for BE compat. */
  lunch_label?: string;
  /** G3.6 BE-1 — `null` ⇒ no per-day cap; the solver's hard
   * constraint is skipped and the workload-balance soft constraint
   * handles distribution. Defaults to 5h to match the platform
   * recommendation. */
  teacher_max_hours_per_day?: number | null;
  /** G3.6 BE-1 — `null` ⇒ no per-week cap. */
  teacher_max_hours_per_week?: number | null;
  soft_constraints?: SoftConstraintTogglesPayload | null;
}

/** Mirrors :class:`QuickGenerateRequest` on the BE — the body for
 * `POST /admin/scheduling/timetable-jobs:from-catalogue`.
 *
 * G3.5 BE-2 — validity controls (`validity_kind` + `valid_from` +
 * `valid_until`). All three are optional; when omitted the BE
 * stamps `YEARLY` from `date.today()`. `valid_until` is REQUIRED
 * when `validity_kind === "custom"` and must be strictly after
 * `valid_from` when both are supplied. */
export interface QuickGenerateRequest {
  name: string;
  description?: string | null;
  branch_id: string;
  academic_year_id: string;
  config?: QuickGenerateConfigPayload;
  validity_kind?: ValidityKind;
  /** Local-date string `YYYY-MM-DD`. */
  valid_from?: string | null;
  /** Local-date string `YYYY-MM-DD`. Required when
   * `validity_kind === "custom"`. */
  valid_until?: string | null;
}

// --- Phase G3.5 — Calendar / Holiday domain ---------------------------
//
// Mirrors `eduviaos-backend/app/domains/calendar/{models,schemas}.py`.
// The IA admin authoring surface lives at `/holidays` (FE-2). Other
// downstream consumers (timetable solver, attendance roll-call) read
// the same `Holiday` rows.

/** Phase G3.5 BE-3 — semantic taxonomy for a holiday entry. The FE
 * uses this to group rows by category in the listing and to badge
 * each row with the right icon. */
export type HolidayKind =
  | "national"
  | "regional"
  | "religious"
  | "school_event"
  | "exam_day"
  | "weather"
  | "other";

/** POST / PATCH body for `/admin/calendar/holidays`. Mirrors
 * :class:`HolidayPayload`. */
export interface HolidayPayload {
  /** `null` = tenant-wide holiday (all branches honour it). */
  branch_id?: string | null;
  name: string;
  description?: string | null;
  kind?: HolidayKind;
  /** Local-date string `YYYY-MM-DD`. */
  start_date: string;
  /** Local-date string `YYYY-MM-DD` — inclusive upper bound for
   * multi-day events. `null` ⇒ single-day. */
  end_date?: string | null;
  /** Inverse of "is closed" — `true` when the school stays open
   * (e.g. a make-up class on a Sunday). Default `false`. */
  is_school_open?: boolean;
}

/** Single :class:`Holiday` projection for the FE. */
export interface HolidayView {
  id: string;
  tenant_id: string;
  branch_id: string | null;
  name: string;
  description: string | null;
  kind: HolidayKind;
  start_date: string;
  end_date: string | null;
  is_school_open: boolean;
  /** Set when the row was materialised from a catalogue suggestion. */
  suggestion_id: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface HolidayList {
  holidays: HolidayView[];
  total: number;
}

/** One curated suggestion from the India holidays catalogue. */
export interface HolidaySuggestion {
  id: string;
  name: string;
  description: string;
  kind: HolidayKind;
  start_date: string;
  end_date: string | null;
  region_hint: string | null;
}

export interface HolidaySuggestionList {
  suggestions: HolidaySuggestion[];
  total: number;
}

/** POST body for `/admin/calendar/holidays:bulk-import`. Idempotent
 * — already-imported suggestions are silently skipped and reported
 * back in `skipped_suggestion_ids`. */
export interface HolidayBulkImportRequest {
  branch_id?: string | null;
  /** 1..200 suggestion IDs. */
  suggestion_ids: string[];
}

export interface HolidayBulkImportResponse {
  imported: HolidayView[];
  skipped_suggestion_ids: string[];
  unknown_suggestion_ids: string[];
}

// --- Assessment domain (Phase H5 — mirrors
// `app.domains.assessment.{types,schemas,template}.py`) ------------------
//
// Question taxonomy, polymorphic answers, blueprint items, and templates.
// Hand-authored to match BE Pydantic models exactly. Drift between this
// file and the BE catalog is caught by typecheck against the live API
// during stage smoke runs; a generator drift test lands in a follow-up.
//
// Iron Rule #2: every concept on the BE has a real shape here (no
// `Record<string, any>` shrugs). The `AssessmentCorrectAnswer`
// discriminated union mirrors the BE's `answer_kind`-keyed Pydantic
// union 1:1 so the FE can render type-aware authoring + read views
// without ad-hoc dict probes.

export type QuestionType =
  | "mcq_single"
  | "mcq_multi"
  | "yes_no"
  | "true_false"
  | "short_answer"
  | "long_answer"
  | "latex_equation"
  | "code"
  | "file_upload"
  | "image_question_text_options"
  | "image_question_image_options"
  | "numeric"
  | "match_following"
  | "fill_in_the_blank";

export type QuestionDifficulty = "easy" | "medium" | "hard" | "expert";

export type QuestionLanguage = "en" | "hi";

export type ShortAnswerMatchStrategy = "exact" | "case_insensitive" | "regex";

export type MCQMultiPartialCreditRule =
  | "all_or_nothing"
  | "per_option_fraction"
  | "negative_for_wrong";

export type AssessmentGenerationMode = "pre_gen" | "on_demand" | "hybrid";

export type AssessmentProctorProfile = "none" | "basic" | "strict" | "strict_plus_id";

export type AssessmentTemplateStatus = "draft" | "active" | "archived";

export type AssessmentQuestionStatus = "draft" | "approved" | "archived";

export interface AssessmentGradeBand {
  /** Inclusive min, 1..12. */
  min: number;
  /** Inclusive max, 1..12, ≥ min. */
  max: number;
}

export interface AssessmentQuestionOption {
  /** Stable identifier referenced by the response payload. */
  id: string;
  label_richtext?: string | null;
  image_url?: string | null;
  latex?: string | null;
  is_correct?: boolean;
}

// ─── CorrectAnswer discriminated union ─────────────────────────────────

export interface MCQSingleAnswer {
  answer_kind: "mcq_single";
  option_id: string;
}

export interface MCQMultiAnswer {
  answer_kind: "mcq_multi";
  option_ids: string[];
  partial_credit_rule?: MCQMultiPartialCreditRule;
}

export interface YesNoAnswer {
  answer_kind: "yes_no";
  value: boolean;
}

export interface TrueFalseAnswer {
  answer_kind: "true_false";
  value: boolean;
}

export interface ShortAnswer {
  answer_kind: "short_answer";
  accepted: string[];
  match?: ShortAnswerMatchStrategy;
}

export interface LongAnswer {
  answer_kind: "long_answer";
  rubric_id?: string | null;
  manual_grading_required: true;
}

export interface LatexAnswer {
  answer_kind: "latex_equation";
  accepted: string[];
  canonical_form_check?: boolean;
}

export interface CodeTestCase {
  input: string;
  expected_output: string;
  weight?: number;
  is_hidden?: boolean;
}

export interface CodeAnswer {
  answer_kind: "code";
  language: "python" | "javascript" | "java" | "cpp" | "c";
  test_cases: CodeTestCase[];
  time_limit_ms?: number;
  memory_limit_kb?: number;
}

export interface FileUploadAnswer {
  answer_kind: "file_upload";
  accepted_mime_types: string[];
  max_size_bytes?: number;
  rubric_id?: string | null;
  manual_grading_required: true;
}

export interface NumericAnswer {
  answer_kind: "numeric";
  value: number;
  tolerance_abs?: number | null;
  tolerance_pct?: number | null;
  unit?: string | null;
}

export interface MatchFollowingPair {
  left_id: string;
  right_id: string;
}

export interface MatchFollowingAnswer {
  answer_kind: "match_following";
  pairs: MatchFollowingPair[];
}

export interface FillInTheBlankAnswer {
  answer_kind: "fill_in_the_blank";
  blanks: ShortAnswer[];
}

export type AssessmentCorrectAnswer =
  | MCQSingleAnswer
  | MCQMultiAnswer
  | YesNoAnswer
  | TrueFalseAnswer
  | ShortAnswer
  | LongAnswer
  | LatexAnswer
  | CodeAnswer
  | FileUploadAnswer
  | NumericAnswer
  | MatchFollowingAnswer
  | FillInTheBlankAnswer;

// ─── Topic ─────────────────────────────────────────────────────────────

export interface AssessmentTopicPublic {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  subject: string;
  description: string | null;
  parent_slug: string | null;
  grade_band: AssessmentGradeBand | null;
  archived_reason: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentTopicListResponse {
  items: AssessmentTopicPublic[];
  total: number;
}

export interface AssessmentTopicCreate {
  name: string;
  slug: string;
  subject: string;
  description?: string | null;
  parent_slug?: string | null;
  grade_band?: AssessmentGradeBand | null;
}

export type AssessmentTopicUpdate = Partial<Omit<AssessmentTopicCreate, "slug">>;

// ─── Question ──────────────────────────────────────────────────────────

export interface AssessmentQuestionPublic {
  id: string;
  tenant_id: string;
  type: QuestionType;
  prompt_richtext: string;
  prompt_latex: string | null;
  prompt_image_url: string | null;
  solution_richtext: string | null;
  options: AssessmentQuestionOption[];
  correct_answer: AssessmentCorrectAnswer;
  topic_slug: string | null;
  grade_band: AssessmentGradeBand;
  difficulty: QuestionDifficulty;
  language: QuestionLanguage;
  marks: number;
  negative_marks: number;
  status: AssessmentQuestionStatus;
  archived_reason: string | null;
  approved_at: string | null;
  approved_by_user_id: string | null;
  origin: "manual" | "ai" | "imported";
  ai_call_log_id: string | null;
  ai_model_id: string | null;
  tags: string[];
  created_by_user_id: string | null;
  last_modified_by_user_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface AssessmentQuestionListResponse {
  items: AssessmentQuestionPublic[];
  total: number;
}

export interface AssessmentQuestionListFilters {
  topic_slug?: string;
  type?: QuestionType;
  difficulty?: QuestionDifficulty;
  language?: QuestionLanguage;
  grade?: number;
  status?: AssessmentQuestionStatus;
  origin?: "manual" | "ai" | "imported";
  include_archived?: boolean;
  limit?: number;
  skip?: number;
}

// ─── Blueprint + Template ──────────────────────────────────────────────

export interface AssessmentBlueprintItem {
  topic_slugs: string[];
  count: number;
  type: QuestionType;
  difficulty?: QuestionDifficulty | null;
  language?: QuestionLanguage | null;
  marks_per_question?: number;
  negative_marks?: number | null;
  randomise_options?: boolean;
  require_human_authored?: boolean;
}

export interface AssessmentSectionLimit {
  section_index: number;
  seconds: number;
}

export interface AssessmentTemplatePublic {
  id: string;
  tenant_id: string;
  name: string;
  slug: string;
  description: string | null;
  grade_band: AssessmentGradeBand;
  applies_to_courses: string[];
  language: QuestionLanguage;
  items: AssessmentBlueprintItem[];
  generation_mode: AssessmentGenerationMode;
  pre_gen_pool_size: number;
  pre_gen_set_size: number | null;
  ai_top_up_threshold_pct: number;
  total_seconds: number;
  section_time_limits: AssessmentSectionLimit[];
  max_attempts: number;
  allow_pause: boolean;
  shuffle_questions: boolean;
  shuffle_options_within_question: boolean;
  calculator_allowed: boolean;
  language_picker_enabled: boolean;
  proctor_profile: AssessmentProctorProfile;
  outcome_policy: Record<string, unknown> | null;
  show_correct_answers_in_report: boolean;
  apply_portal_show_results_after_outcome: boolean;
  status: AssessmentTemplateStatus;
  archived_reason: string | null;
  activated_at: string | null;
  activated_by_user_id: string | null;
  created_by_user_id: string | null;
  last_modified_by_user_id: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
  /** Computed sum of `items[].count * items[].marks_per_question`. */
  total_marks: number;
}

export interface AssessmentTemplateListResponse {
  items: AssessmentTemplatePublic[];
  total: number;
}

export interface AssessmentTemplateListFilters {
  status?: AssessmentTemplateStatus;
  grade?: number;
  language?: QuestionLanguage;
  include_archived?: boolean;
  search?: string;
  limit?: number;
  skip?: number;
}

export interface AssessmentTemplateCreate {
  name: string;
  slug: string;
  description?: string | null;
  grade_band: AssessmentGradeBand;
  applies_to_courses?: string[];
  language?: QuestionLanguage;
  items: AssessmentBlueprintItem[];
  generation_mode?: AssessmentGenerationMode;
  pre_gen_pool_size?: number;
  ai_top_up_threshold_pct?: number;
  total_seconds?: number;
  section_time_limits?: AssessmentSectionLimit[];
  max_attempts?: number;
  allow_pause?: boolean;
  shuffle_questions?: boolean;
  shuffle_options_within_question?: boolean;
  calculator_allowed?: boolean;
  language_picker_enabled?: boolean;
  proctor_profile?: AssessmentProctorProfile;
  outcome_policy?: Record<string, unknown> | null;
  show_correct_answers_in_report?: boolean;
  apply_portal_show_results_after_outcome?: boolean;
}

export type AssessmentTemplateUpdate = Partial<Omit<AssessmentTemplateCreate, "slug">>;

export interface AssessmentBlueprintAvailabilityRow {
  index: number;
  topic_slugs: string[];
  type: QuestionType;
  difficulty: QuestionDifficulty | null;
  requested: number;
  available: number;
  deficit: number;
}

export interface AssessmentBlueprintAvailabilityResponse {
  rows: AssessmentBlueprintAvailabilityRow[];
  has_deficit: boolean;
}

// ─── Phase F11 — AI question generation (Question Bank) ────────────────
//
// Wire shapes for the F9 AI generation endpoints, surfaced on the FE in
// Phase F11:
//
//   POST /assessments/topics/{topic_slug}/generate-questions:batch
//   POST /assessments/questions/{question_id}:regenerate
//
// Match `GenerateQuestionsRequest` / `GenerateQuestionsResponse` /
// `RegenerateQuestionRequest` / `RegenerateQuestionResponse` in
// `eduviaos-backend/app/domains/assessment/schemas.py` 1:1.

/** Question types F9 refuses to AI-generate (BE `_AI_REFUSED_TYPES_FOR_BATCH`).
 *  Keep in sync if the BE list changes; the FE filters its Select against
 *  this set so the user never picks a type the server will 422 on. */
export const AI_GENERATABLE_QUESTION_TYPES: readonly QuestionType[] = [
  "mcq_single",
  "mcq_multi",
  "yes_no",
  "true_false",
  "short_answer",
  "long_answer",
  "latex_equation",
  "numeric",
  "fill_in_the_blank",
  "match_following",
] as const;

export interface AssessmentGenerateQuestionsRequest {
  type: QuestionType;
  grade_band: AssessmentGradeBand;
  difficulty?: QuestionDifficulty;
  language?: QuestionLanguage;
  /** 1-25, server caps at the MAX_BATCH_SIZE (25). */
  count?: number;
  locale?: string;
  marks?: number;
  negative_marks?: number;
  extra_instructions?: string | null;
}

export interface AssessmentGenerateQuestionsResponse {
  /** Persisted drafts (status=draft, origin=ai). Ready for review. */
  generated: AssessmentQuestionPublic[];
  requested_count: number;
  accepted_count: number;
  rejected_count: number;
  /** Per-row rejection reasons (e.g. `"#3: type mismatch …"`). */
  rejection_reasons: string[];
  /** `null` on success; short hint when AI output wasn't parseable. */
  parse_error: string | null;
  provider: string;
  model: string;
  /** 0 = primary, 1 = first fallback, etc. Drives the "Resilience mode" pill. */
  fallback_depth: number;
  latency_ms: number;
}

export interface AssessmentRegenerateQuestionRequest {
  extra_instructions?: string | null;
}

export interface AssessmentRegenerateQuestionResponse {
  /** Regenerated row — still status=draft, origin=ai, same `id`. */
  question: AssessmentQuestionPublic;
  parse_error: string | null;
  rejection_reasons: string[];
  provider: string;
  model: string;
  fallback_depth: number;
  latency_ms: number;
  ai_call_log_id: string | null;
  generated_at: string;
}

// ─── Phase H5b — SA cross-tenant assessment template library ───────────
//
// Wire shapes for the SA-scoped routes in
// `app/domains/assessment/admin_routes.py`:
//
//   GET  /super-admin/assessment-templates
//   GET  /super-admin/tenants/{tenant_slug}/assessment-templates
//   POST /super-admin/assessment-templates/{template_id}/clone
//
// The list rows mirror `SuperAdminTemplateRow` in
// `app/domains/assessment/schemas.py` 1:1 — lighter than
// `AssessmentTemplatePublic` (drops blueprint items + runtime knobs the
// matrix view doesn't need) and joins in the tenant's human-readable
// `tenant_name` so the SA library can render
// `Acme School / grade-6-entrance` without a second round trip.

export interface SuperAdminAssessmentTemplateRow {
  id: string;
  /** Tenant slug — matches the tenant's stable identifier. */
  tenant_id: string;
  /** Human-readable tenant name resolved at read time. `null` if the
   *  template row outlived its tenant (defensive — we don't soft-delete
   *  tenants in Phase H but the SA library shouldn't crash if it ever
   *  happens). */
  tenant_name: string | null;
  name: string;
  slug: string;
  description: string | null;
  /** `"draft" | "active" | "archived"` — wider string for forward-compat
   *  in case the BE adds a new status, but indexed by
   *  `AssessmentTemplateStatus` in render code today. */
  status: AssessmentTemplateStatus;
  grade_band: AssessmentGradeBand;
  language: QuestionLanguage;
  /** Number of blueprint items — surfaced for the matrix view so the
   *  SA can spot empty / overweight templates at a glance. */
  item_count: number;
  /** Computed total marks. Mirrors
   *  `AssessmentTemplatePublic.total_marks`. */
  total_marks: number;
  total_seconds: number;
  proctor_profile: AssessmentProctorProfile;
  activated_at: string | null;
  deleted_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface SuperAdminAssessmentTemplateListResponse {
  items: SuperAdminAssessmentTemplateRow[];
  total: number;
}

export interface SuperAdminAssessmentTemplateListFilters {
  /** Cross-tenant endpoint only — restrict to one tenant slug. The
   *  per-tenant endpoint pins this from the path param. */
  tenant_id?: string;
  status?: AssessmentTemplateStatus;
  grade?: number;
  language?: QuestionLanguage;
  include_archived?: boolean;
  search?: string;
  limit?: number;
  skip?: number;
}

export interface SuperAdminAssessmentTemplateCloneRequest {
  /** Slug of the tenant that should receive the clone. Cross-tenant
   *  moves are explicit; cloning into the source tenant is allowed for
   *  "fork-as-starter" workflows but requires a distinct `new_slug`. */
  target_tenant_slug: string;
  /** Slug for the cloned template. Must satisfy the standard slug
   *  pattern and be unique among active rows in the target tenant. */
  new_slug: string;
  /** Optional override for the human-readable name. Defaults to the
   *  source template's `name` when omitted. */
  new_name?: string | null;
}

export interface SuperAdminAssessmentTemplateCloneResponse {
  template: AssessmentTemplatePublic;
  source_template_id: string;
  /** Source tenant slug — surfaces in the audit-friendly
   *  "cloned from Acme / grade-6-entrance" line. */
  source_tenant_id: string;
}

// ─── UI label catalogs (Iron Rule #5 — render order is canonical) ──────

export const QUESTION_TYPE_ORDER: QuestionType[] = [
  "mcq_single",
  "mcq_multi",
  "yes_no",
  "true_false",
  "short_answer",
  "long_answer",
  "latex_equation",
  "numeric",
  "fill_in_the_blank",
  "match_following",
  "image_question_text_options",
  "image_question_image_options",
  "code",
  "file_upload",
];

export const QUESTION_TYPE_LABEL: Record<QuestionType, string> = {
  mcq_single: "MCQ — single answer",
  mcq_multi: "MCQ — multi-select",
  yes_no: "Yes / No",
  true_false: "True / False",
  short_answer: "Short answer",
  long_answer: "Long answer (essay)",
  latex_equation: "LaTeX equation",
  numeric: "Numeric",
  fill_in_the_blank: "Fill in the blanks",
  match_following: "Match the following",
  image_question_text_options: "Image prompt + text options",
  image_question_image_options: "Image prompt + image options",
  code: "Code (sandboxed)",
  file_upload: "File upload",
};

export const QUESTION_DIFFICULTY_ORDER: QuestionDifficulty[] = ["easy", "medium", "hard", "expert"];

export const QUESTION_DIFFICULTY_LABEL: Record<QuestionDifficulty, string> = {
  easy: "Easy",
  medium: "Medium",
  hard: "Hard",
  expert: "Expert",
};

export const QUESTION_LANGUAGE_LABEL: Record<QuestionLanguage, string> = {
  en: "English",
  hi: "Hindi",
};

export const ASSESSMENT_GENERATION_MODE_LABEL: Record<AssessmentGenerationMode, string> = {
  pre_gen: "Pre-generated pool",
  on_demand: "On-demand (AI)",
  hybrid: "Hybrid (pool → AI fallback)",
};

export const ASSESSMENT_GENERATION_MODE_DESCRIPTION: Record<AssessmentGenerationMode, string> = {
  pre_gen: "Always pull from a pre-warmed pool. Fastest, but a depleted pool blocks new attempts.",
  on_demand: "Generate fresh questions per attempt via AI. Slower start, always unique.",
  hybrid:
    "Try the pre-warmed pool first; fall through to AI when empty. Recommended default — keeps spikes unblocked.",
};

export const ASSESSMENT_PROCTOR_PROFILE_LABEL: Record<AssessmentProctorProfile, string> = {
  none: "No proctoring",
  basic: "Basic — focus + fullscreen tracking",
  strict: "Strict — webcam + activity heuristics",
  strict_plus_id: "Strict + ID verification",
};

// =====================================================
// Phase J3 — Parents data plane (mirrors of
//   eduviaos-backend/app/domains/parents/schemas.py::
//     ParentResponse / ParentListResponse / StudentRefResponse /
//     ParentStudentsResponse)
//
// Reads on `view_students`; writes on `manage_students` per BE
// `eduviaos-backend/app/domains/parents/routes.py`. The IA UI in J3
// is list-only — writes (Create / Edit) ship in J3.b once the row
// actions are sanity-checked against real data.
// =====================================================

export type ParentRelationship = "father" | "mother" | "guardian" | "other";

export interface Parent {
  id: string;
  tenant_id: string;
  branch_id: string | null;

  name: string;
  relationship: ParentRelationship;
  email: string | null;
  phone: string;
  alt_phone: string | null;

  user_id: string | null;
  is_phone_verified: boolean;
  is_email_verified: boolean;
  consent_given_at: string | null;

  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  state: string | null;
  pincode: string | null;

  pan_reference: string | null;
  occupation: string | null;

  metadata: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface ParentListFilters {
  branch_id?: string;
  relationship?: ParentRelationship;
  limit?: number;
  offset?: number;
}

export interface ParentListResponse {
  items: Parent[];
  total: number;
}

/**
 * Compact student reference returned by `GET /api/parents/{id}/students`.
 * The full Student doc is reachable via `GET /api/students/{id}` and the
 * `Student` interface above.
 */
export interface ParentStudentRef {
  id: string;
  name: string;
  admission_number: string | null;
  grade: string | null;
  section: string | null;
  status: StudentStatus;
}

export interface ParentStudentsResponse {
  parent_id: string;
  students: ParentStudentRef[];
  count: number;
}

// =====================================================
// Phase J3.b — Parents writes (mirrors of
//   eduviaos-backend/app/domains/parents/schemas.py::
//     CreateParentRequest / UpdateParentRequest)
//
// The parents BE has NO soft-delete or status-transition endpoint —
// lifecycle is implicit via the linked `Student.parent_id`. So the
// J3.b surface is Create + Edit only; no `ParentTransitionInput` or
// `ParentDeleteInput` to plan for.
// =====================================================

/**
 * Body for `POST /api/parents/`. `name`, `phone` are required; everything
 * else is optional. `relationship` defaults to `"guardian"` on the BE
 * when omitted; the FE form surfaces the Select so the IA admin picks
 * it explicitly (clearer intent + better audit trail).
 */
export interface ParentCreateInput {
  name: string;
  phone: string;
  relationship?: ParentRelationship;
  email?: string | null;
  alt_phone?: string | null;
  branch_id?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  pan_reference?: string | null;
  occupation?: string | null;
  metadata?: Record<string, unknown>;
}

/**
 * Body for `PATCH /api/parents/{id}`. All fields are optional; only
 * changed fields are sent (diff-only PATCH). The BE supports flipping
 * `is_phone_verified` / `is_email_verified` via PATCH — surfacing those
 * to the IA admin is intentional: admins can mark a parent's phone
 * verified after an out-of-band call so OTP-based parent-portal flows
 * unblock without forcing a re-OTP cycle.
 */
export interface ParentUpdateInput {
  name?: string;
  relationship?: ParentRelationship;
  email?: string | null;
  phone?: string;
  alt_phone?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  pincode?: string | null;
  pan_reference?: string | null;
  occupation?: string | null;
  user_id?: string | null;
  is_phone_verified?: boolean;
  is_email_verified?: boolean;
  metadata?: Record<string, unknown> | null;
}

// --- Staff directory (Phase J2) ------------------------------------------
//
// Wire types for `GET /me/staff` — the People > Staff DataTable's
// data source. Mirrors `app/domains/staff_roster/schemas.py` on the
// backend exactly so the FE and BE stay in lock-step.
//
// NOTE: We use the `StaffDirectory*` prefix (not `StaffRoster*`) to
// avoid colliding with the older Phase D5e `StaffRosterItem` /
// `StaffRosterResponse` types — those are the admissions-assignee
// shape (`user_id`, no invite metadata) consumed by the AssignmentMenu
// / CollaboratorsCard / BulkActionBar. Keep the namespaces separate.

/**
 * Subset of {@link UserRole} that participates in the staff directory.
 * Students / parents / super-admins are never surfaced here.
 */
export type StaffDirectoryRole = "owner" | "admin" | "teacher" | "staff";

/**
 * Surfaced status for a directory row. Soft-deleted rows are filtered
 * server-side so {@link UserStatus}'s `deleted` value never reaches the
 * wire.
 */
export type StaffDirectoryStatus = "active" | "suspended" | "pending_verification";

/**
 * Lifecycle of the most recent {@link StaffInvite} row matching the
 * user's `(tenant_id, email)`. `expired` is derived at read time on
 * the BE from `expires_at < now()` while persisted status is
 * `pending` — the DB never stores it.
 *
 * `null` means no relevant invite row exists (e.g. the user was
 * created via direct registration or by the seed).
 */
export type StaffDirectoryInviteStatus = "pending" | "accepted" | "expired" | "revoked";

export interface StaffDirectoryEntry {
  id: string;
  tenant_id: string;
  email: string;
  full_name: string | null;
  role: StaffDirectoryRole;
  status: StaffDirectoryStatus;
  designation: string | null;
  designation_id: string | null;
  access_inherits_designation: boolean;
  permission_mode: PermissionMode;
  permission_overrides: string[];
  branch_id: string | null;
  /** RFC3339. Null when the user has never authenticated. */
  last_login_at: string | null;
  /** RFC3339. Stable insertion timestamp; useful as a default sort. */
  created_at: string;
  invite_status: StaffDirectoryInviteStatus | null;
  /**
   * Stringified `StaffInvite._id` of the most recent invite row when
   * `invite_status` is non-null; `null` when no invite row exists for
   * the user. Surfaced in Phase J2.b so the FE Staff tab's row action
   * menu can call
   * `POST /me/first-run/staff-invites/{invite_id}/{resend,revoke}`
   * without an extra lookup round-trip. Always present whenever an
   * invite row exists (regardless of `invite_status`) — even for
   * accepted / revoked invites — so future audit-style affordances
   * (e.g. "view invite history") don't need another BE change.
   */
  invite_id: string | null;
  /**
   * Only set when `invite_status in {"pending", "expired"}`. Null for
   * accepted, revoked, or no-invite rows.
   */
  invite_expires_at: string | null;
  /**
   * Phase J2.c — number of invite emails sent so far for the most
   * recent {@link StaffInvite} row.
   *
   * - `null`  → user has no invite row at all
   * - `0`     → invite row exists but no email was dispatched (e.g.
   *              future draft-invite flow)
   * - `>= 1`  → N emails sent (incremented by the resend pipeline)
   *
   * The FE relies on the `null` vs `0` distinction to decide whether
   * to render the sub-label at all, so we explicitly preserve both
   * sentinels on the wire.
   */
  invite_email_send_count: number | null;
  /**
   * Phase J2.c — RFC3339 UTC timestamp of the most recent invite
   * email send. `null` whenever `invite_email_send_count` is `null`
   * or `0`. The FE pairs this with `Date.now()` to render a
   * "last 2h ago" relative-time tag next to the send count.
   */
  invite_last_email_sent_at: string | null;
}

export interface StaffDirectoryFilters {
  role?: StaffDirectoryRole;
  status?: StaffDirectoryStatus;
  branch_id?: string;
  /** Case-insensitive substring across `full_name` + `email`. */
  q?: string;
  /** Page size, 1-200. */
  limit?: number;
  /** Page offset, >= 0. */
  offset?: number;
}

export interface StaffDirectoryResponse {
  items: StaffDirectoryEntry[];
  total: number;
}

export interface StaffAccessUpdateInput {
  designation_id?: string | null;
  role?: StaffDirectoryRole;
  permission_mode?: PermissionMode;
  permission_overrides?: string[] | null;
}

export interface StaffAccessUpdateResponse {
  user: StaffDirectoryEntry;
  inherited_from_designation: boolean;
}

/**
 * Phase J2.b — invite resend / revoke responses are shared with the
 * first-run wizard's "Invite staff" step. See `ResendStaffInviteResponse`
 * + `RevokeStaffInviteResponse` defined above near `PendingStaffInviteItem`.
 */

/**
 * Phase J2.d — request body shared by suspend + reactivate routes.
 *
 * Both routes accept the same optional `reason` shape — a free-text
 * justification capped server-side at 280 chars. The FE only requires
 * a reason on suspend (re-activation is celebratory); both fields
 * tolerate `null` / `""` / `undefined`, all of which the BE normalises
 * to `null` before writing to the {@link AuditLog} row.
 *
 * Sending a reason longer than 280 chars yields a `422` from the BE
 * (FastAPI's Pydantic constraint). The FE pre-validates with the same
 * cap to keep the UX snappy.
 */
export interface StaffStatusChangeRequest {
  /** Free-text justification, server-truncated to 280 chars. */
  reason?: string | null;
}

/**
 * Phase J2.d — response envelope from
 * `POST /me/staff/{user_id}/{suspend|reactivate}`.
 *
 * Carries the post-flip {@link StaffDirectoryEntry} so the FE can
 * patch its TanStack cache by `user.id` without a re-fetch, plus a
 * stringified {@link AuditLog} id for future "audit history" UX.
 */
export interface StaffStatusChangeResponse {
  user: StaffDirectoryEntry;
  /** Stringified `AuditLog._id` of the row written for this transition. */
  audit_log_id: string;
}

// --- Domain re-exports ---------------------------------------------------

export type * from "./forms.js";
export { DISPLAY_ONLY_FIELD_TYPES, FIELD_TYPE_LABEL, FIELD_TYPE_ORDER } from "./forms.js";

export type * from "./public-admissions.js";

export type * from "./proctor-review.js";

export type * from "./capabilityAi.js";
export {
  MAX_PROMPT_LENGTH,
  MIN_PROMPT_LENGTH,
  MAX_STAGES_PER_FUNNEL,
  MAX_REQUIREMENTS_PER_STAGE,
  MAX_FIELDS_PER_FUNNEL_SUGGESTION,
} from "./capabilityAi.js";

// --- Learner app commercial (Phase SC-4) -----------------------------------

/** How a study centre prices the learner app relative to the seat. */
export type LearnerAppPricingMode = "bundled" | "separate" | "sa_push";

/** Who collects the learner-app fee. */
export type LearnerAppPaymentOwner = "eduviaos" | "centre";

export interface LearnerAppConfigResponse {
  pricing_mode: LearnerAppPricingMode;
  /** Monthly price in paise; 0 when bundled. */
  monthly_price_paise: number;
  /** Platform commission in basis points (0–10000). */
  commission_bps: number;
  payment_owner: LearnerAppPaymentOwner;
  /** Centre Razorpay Key ID. Empty when EduViaOS collects. */
  razorpay_key_id: string;
  razorpay_key_secret_set: boolean;
  razorpay_webhook_secret_set: boolean;
}

export interface LearnerAppConfigUpdateRequest {
  pricing_mode: LearnerAppPricingMode;
  monthly_price_paise: number;
  commission_bps: number;
  payment_owner?: LearnerAppPaymentOwner;
  razorpay_key_id?: string;
  /** Write-only. Omit to keep the stored secret. */
  razorpay_key_secret?: string;
  /** Write-only. Omit to keep the stored webhook secret. */
  razorpay_webhook_secret?: string;
}

export type PlatformRazorpaySource = "stored" | "environment" | "unset";

export interface PlatformRazorpayResponse {
  key_id: string;
  key_secret_set: boolean;
  webhook_secret_set: boolean;
  source: PlatformRazorpaySource;
}

export interface PlatformRazorpayReplaceRequest {
  key_id: string;
  key_secret?: string;
  webhook_secret?: string;
}

export interface AppAccessActionResponse {
  student_id: string;
  app_access_granted: boolean;
}
